import dayjs, { Dayjs } from 'dayjs';
import CommonJs from '@/utils/commonJs';


import {
  ViewDashboardDuAn,
  ItemDashboardDuAn,
  DuAnCongViecTienDoTree,
  DuAnCongViecChiTieuItem,
  CongViecDuAnCheck,
  DuAnCongViecTree,
  RoleItemTree,
} from '@/types/action-plan/DuAnView';
import { Buoc6PhuongDien } from '@/types/performance/buoc6phuongdien';
import { DuAnCongViec } from '@/types/action-plan/ActionPlan/DuAnCongViec';
import {
  formatActionPlanDateDisplay,
  formatDateOnlyLocalYMD,
  normalizeListChiTieuNgayForApi,
  actionPlanCalendarYMD,
  toActionPlanCalendarDate,
} from '@/utils/dateOnlyForApi';
import { TableRecord, TableRecordKanbanFieldPatch } from '../CongViecDuAn/Right/Table/types';
import { CONSTANTS } from '@/utils/constants';
import { CryptoService } from '@/utils/cryptoService';
import { DEFAULT_ROLE_ITEM_TREE, DEFAULT_TIEN_DO_TREE, Task } from '../defaultModels';
import { KanbanBoardRulesState } from '@/types/action-plan/ActionPlan/UpdateKanbanBoardRulesRequest';
import { MutableRefObject } from 'react';
import { DuAnCongViecDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecDTO';
import { ProfileUser } from '@/types/authen';
import { DuAnCongViecNganSachDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecNganSachDTO';
import {
  getEffectiveWeightVer1,
  getEffectiveWeightVer2,
  apv2IsLockedManualTiTrong,
} from '@/components/common/ActionPlanV2/core/tiTrong';


export const SESSION_KEY = CONSTANTS.ACTION_PLAN.SESSION_KEY;

//----------------------------------------------------------------------------------------|
//--------------------------------------DỰ ÁN --------------------------------------------|
//----------------------------------------------------------------------------------------|

/**
 * Chuẩn hóa tab URL về dạng '1' | '2' | '3'
 */
 
export const tabCharToNum = (tab?: string): string => {
  if (!tab) return '1';
  if (tab === 'pt') return '1';
  if (tab === 'dg') return '2';
  if (tab === 'ht') return '3';
  return tab || '1';
};

/**
 * Get tab number to character
 */
export const getTabNumToChar = (tab: string): string => {  
  if (tab === '1') return 'pt';
  if (tab === '2') return 'dg';
  if (tab === '3') return 'ht';
  return tab || 'pt';
};

/**
 * Format date range from RangePicker to "DD/MM/YYYY - DD/MM/YYYY"
 */
export const formatDateRange = (dates: [Dayjs, Dayjs] | null): string | null => {
  if (!dates || !dates[0] || !dates[1]) {
    return null;
  }
  const formatDate = (date: Dayjs): string => {
    return date.format('DD/MM/YYYY');
  };
  return `${formatDate(dates[0])} - ${formatDate(dates[1])}`;
};

/**
 * Chuẩn hóa dateChoice từ URL (Next/query có thể giữ `+` thay khoảng trắng).
 * Ví dụ: `22/07/2026+-+31/08/2026` → `22/07/2026 - 31/08/2026`
 */
export const normalizeDateChoiceQuery = (raw: string | null | undefined): string => {
  if (raw == null || raw === '') return '';
  let s = String(raw).trim();
  try {
    if (/%[0-9A-Fa-f]{2}/.test(s)) s = decodeURIComponent(s);
  } catch {
    // ignore malformed encoding
  }
  s = s.replace(/\+/g, ' ').replace(/\s+/g, ' ').trim();

  // Chỉ chuẩn hóa separator giữa 2 ngày — không đụng dấu '-' trong DD-MM-YYYY
  const datePart = '(\\d{1,2}[/\\-]\\d{1,2}[/\\-]\\d{4}|\\d{4}-\\d{2}-\\d{2})';
  const pair = s.match(new RegExp(`^${datePart}\\s*[–—\\-]\\s*${datePart}$`));
  if (pair) return `${pair[1]} - ${pair[2]}`;
  return s;
};

/**
 * Query → chuỗi hiển thị/API `DD/MM/YYYY - DD/MM/YYYY`.
 * Hỗ trợ compact `DD-MM-YYYY-DD-MM-YYYY` và legacy có space/`+`.
 */
export const resolveDateChoiceDisplayFromQuery = (raw: string | null | undefined): string => {
  if (raw == null || raw === '') return '';
  const trimmed = String(raw).trim();
  if (!trimmed || trimmed === 'nulldate') return '';

  // Compact URL (không space): 22-07-2026-31-08-2026
  const compactParts = trimmed.replace(/\+/g, '-').split('-').filter(Boolean);
  if (compactParts.length === 6 && !trimmed.includes('/')) {
    const [d1, m1, y1, d2, m2, y2] = compactParts;
    return `${d1}/${m1}/${y1} - ${d2}/${m2}/${y2}`;
  }

  const normalized = normalizeDateChoiceQuery(trimmed);
  if (normalized.includes(' - ')) return normalized;
  return '';
};

/** Parse chuỗi đã lưu (display / compact URL) → tuple cho RangePicker */
export const parseDateRangeToDayjsTuple = (range: string | null | undefined): [Dayjs, Dayjs] | null => {
  const display = resolveDateChoiceDisplayFromQuery(range) || normalizeDateChoiceQuery(range);
  if (!display || !display.includes(' - ')) return null;
  const [left, right] = display.split(' - ').map((s) => s.trim());
  const parsePart = (s: string): Dayjs | null => {
    if (!s) return null;
    // DD/MM/YYYY
    const dmySlash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dmySlash) {
      const iso = `${dmySlash[3]}-${dmySlash[2].padStart(2, '0')}-${dmySlash[1].padStart(2, '0')}`;
      const d0 = dayjs(iso);
      return d0.isValid() ? d0 : null;
    }
    // DD-MM-YYYY (không nhầm với YYYY-MM-DD)
    const dmyDash = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (dmyDash) {
      const iso = `${dmyDash[3]}-${dmyDash[2].padStart(2, '0')}-${dmyDash[1].padStart(2, '0')}`;
      const d0 = dayjs(iso);
      return d0.isValid() ? d0 : null;
    }
    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      const d0 = dayjs(s);
      return d0.isValid() ? d0 : null;
    }
    return null;
  };
  const d1 = parsePart(left);
  const d2 = parsePart(right);
  if (!d1 || !d2) return null;
  return [d1, d2];
};

/** Map tab active (1/2/3 hoặc pt/dg/ht) → API key pt | dg | ht */
export const getTabActiveKey = (tab: string): string => getTabNumToChar(tabCharToNum(tab));

export type DuAnPairSectionLabels = {
  inTargetsCardTitle: string;
  outTargetsCardTitle: string;
  inTargetsPieLabel: string;
  outTargetsPieLabel: string;
};

type DuAnPairTranslate = (key: string, defaultValue: string) => string;

/** Nhãn bảng / pie cột trái–phải theo tab Việc phụ trách | Đã giao | Hỗ trợ */
export const getDuAnPairSectionLabels = (
  activeTab: string,
  t: DuAnPairTranslate
): DuAnPairSectionLabels => {
  const tabKey = getTabActiveKey(activeTab);

  if (tabKey === 'dg') {
    return {
      inTargetsCardTitle: t(
        'bsc.label.danhsachduandagiaoPhuTrach',
        'Danh sách dự án đã giao'
      ),
      outTargetsCardTitle: t(
        'bsc.label.danhsachduandagiaoKhongPhuTrach',
        'Danh sách dự án đã giao'
      ),
      inTargetsPieLabel: t('bsc.label.daGiao', 'Đã giao'),
      outTargetsPieLabel: t('bsc.label.daGiao', 'Đã giao'),
    };
  }

  if (tabKey === 'ht') {
    return {
      inTargetsCardTitle: t(
        'bsc.label.danhsachduanhotroPhuTrach',
        'Danh sách dự án hỗ trợ'
      ),
      outTargetsCardTitle: t(
        'bsc.label.danhsachduanhotroKhongPhuTrach',
        'Danh sách dự án hỗ trợ '
      ),
      inTargetsPieLabel: t('bsc.label.hoTro', 'Hỗ trợ'),
      outTargetsPieLabel: t('bsc.label.hoTro', 'Hỗ trợ'),
    };
  }

  return {
    inTargetsCardTitle: t('bsc.label.danhsachduanphutrach', 'Danh sách dự án phụ trách'),
    outTargetsCardTitle: t('bsc.label.danhsachduanduocgiao', 'Danh sách dự án được giao'),
    inTargetsPieLabel: t('bsc.label.thongkePhuTrach', 'Phụ trách'),
    outTargetsPieLabel: t('bsc.label.thongkeDuocGiao', 'Được giao'),
  };
};

/**
 * Hiển thị khoảng ngày giống {@link DuAnCongViecDatetimePicker} `formatDisplayText`
 * (cùng ngày → DD/MM; cùng tháng → DD - DD/MM; cùng năm → DD/MM - DD/MM; khác năm → full).
 */
export const formatDuAnCongViecDatetimeDisplayText = (start: Dayjs | null, end: Dayjs | null): string => {
  if (!start || !end || !start.isValid() || !end.isValid()) return '';
  const startDay = start.date();
  const startMonth = start.month() + 1;
  const startYear = start.year();
  const endDay = end.date();
  const endMonth = end.month() + 1;
  const endYear = end.year();

  if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
    return `${String(startDay).padStart(2, '0')}/${String(startMonth).padStart(2, '0')}`;
  }

  if (startYear === endYear && startMonth === endMonth) {
    return `${String(startDay).padStart(2, '0')} - ${String(endDay).padStart(2, '0')}/${String(startMonth).padStart(2, '0')}`;
  }

  if (startYear === endYear) {
    return `${String(startDay).padStart(2, '0')}/${String(startMonth).padStart(2, '0')} - ${String(endDay).padStart(2, '0')}/${String(endMonth).padStart(2, '0')}`;
  }

  return `${String(startDay).padStart(2, '0')}/${String(startMonth).padStart(2, '0')}/${startYear} - ${String(endDay).padStart(2, '0')}/${String(endMonth).padStart(2, '0')}/${endYear}`;
};

// Get current tab data based on activeTab
export const getCurrentTabData = (
  dashboardData: ViewDashboardDuAn | null,
  activeTab: string

): ItemDashboardDuAn | null => {
  if (!dashboardData) return null;

  const tabKey = getTabActiveKey(activeTab);
  // Map trực tiếp: tab 1 -> ViecPhuTrach, tab 2 -> ViecDaGiao, tab 3 -> ViecHoTro

  if (tabKey === 'pt') {
    // Merge viecPhuTrach và viecDuocGiao; listTableBSCNEW gộp theo phuongdienID, mục tiêu dedupe theo muctieuID
    const ptList = dashboardData.viecPhuTrach?.listTableBSCNEW || [];
    const dgList = dashboardData.viecDuocGiao?.listTableBSCNEW || [];
    const mergedByPerspective = new Map<string, Buoc6PhuongDien>();

    [...ptList, ...dgList].forEach((pd) => {
      const key = pd.phuongdienID || pd.phuongdienName || '';
      const existing = mergedByPerspective.get(key);
      const listMucTieu = pd.listMucTieu || [];
      if (!existing) {
        mergedByPerspective.set(key, { ...pd, listMucTieu: [...listMucTieu] });
      } else {
        const seenId = new Set((existing.listMucTieu || []).map((m) => m.muctieuID));
        const added = listMucTieu.filter((m) => !seenId.has(m.muctieuID));
        added.forEach((m) => seenId.add(m.muctieuID));
        existing.listMucTieu = [...(existing.listMucTieu || []), ...added];
      }
    });

    return {
      listDuAnCongViec: [
        ...(dashboardData.viecPhuTrach?.listDuAnCongViec || []),
        ...(dashboardData.viecDuocGiao?.listDuAnCongViec || []),
      ],
      listTableBSCNEW: Array.from(mergedByPerspective.values()),

    };
  } else if (tabKey === 'dg') {
    return dashboardData.viecDaGiao;
  } else if (tabKey === 'ht') {
    return dashboardData.viecHoTro;
  }
  return null;
};


// Create map from mtctid to muctieuName for displaying goal names (đã tách count)
export const createMucTieuMap = (listTableBSCNEW: Buoc6PhuongDien[] | undefined): Map<string, string> => {
  if (!listTableBSCNEW) return new Map<string, string>();

  const map = new Map<string, string>();
  listTableBSCNEW.forEach((phuongDien) => {
    phuongDien.listMucTieu?.forEach((mucTieu) => {
      const { name } = extractCountFromName(mucTieu.muctieuName);
      map.set(mucTieu.muctieuID, name);
    });
  });
  return map;
};

// Tạo array chứa thứ tự mtctid theo listMucTieu
export const createListMucTieuOrder = (listTableBSCNEW: Buoc6PhuongDien[] | undefined): string[] | undefined => {
  if (!listTableBSCNEW) return undefined;

  const order: string[] = [];
  listTableBSCNEW.forEach((phuongDien) => {
    phuongDien.listMucTieu?.forEach((mucTieu) => {
      if (mucTieu.muctieuID && !order.includes(mucTieu.muctieuID)) {
        order.push(mucTieu.muctieuID);
      }
    });
  });
  return order.length > 0 ? order : undefined;
};



/** Dự án: theo ngày bắt đầu; công việc: theo vị trí. */
export const compareDuAnCongViecTree = (a: DuAnCongViecTree, b: DuAnCongViecTree): number => {
  const aIsDuAn = Boolean(a.congViec?.isDuAn);
  const bIsDuAn = Boolean(b.congViec?.isDuAn);

  if (aIsDuAn !== bIsDuAn) {
    return aIsDuAn ? -1 : 1;
  }

  if (aIsDuAn) {
    const sa = a.congViec?.ngayBatDau ? dayjs(a.congViec.ngayBatDau).format('YYYY-MM-DD') : '';
    const sb = b.congViec?.ngayBatDau ? dayjs(b.congViec.ngayBatDau).format('YYYY-MM-DD') : '';
    if (sa !== sb) {
      if (!sa) return 1;
      if (!sb) return -1;
      return sa.localeCompare(sb);
    }
  } else {
    const va = a.congViec?.viTri ?? Number.MAX_SAFE_INTEGER;
    const vb = b.congViec?.viTri ?? Number.MAX_SAFE_INTEGER;
    if (va !== vb) return va - vb;
  }

  return (a.congViec?.tenCongViec || '').localeCompare(b.congViec?.tenCongViec || '', 'vi');
};

// Filter + sort listDuAnCongViec by selected target (mtctid) và theo order listMucTieu
export const filterAndSortListDuAnCongViec = <T extends { congViec: { mtctid?: string } }>(
  listDuAnCongViec: T[] | undefined,
  targetSelected: { id: string } | null,
  listMucTieuOrder: string[] | undefined,
  listTableBSCNEW?: Buoc6PhuongDien[]
): T[] => {
  if (!listDuAnCongViec) return [];

  // Bước 0: Tạo Set chứa tất cả muctieuID từ listTableBSCNEW để filter dự án không có mục tiêu
  const validMucTieuIDs = new Set<string>();
  if (listTableBSCNEW && listTableBSCNEW.length > 0) {
    listTableBSCNEW.forEach((phuongDien) => {
      phuongDien.listMucTieu?.forEach((mucTieu) => {
        if (mucTieu.muctieuID) {
          validMucTieuIDs.add(mucTieu.muctieuID);
        }
      });
    });
  }

  // Bước 1: Filter dự án chỉ giữ những dự án có mtctid nằm trong listTableBSCNEW
  let filteredByMucTieu = listDuAnCongViec;
  if (validMucTieuIDs.size > 0) {
    filteredByMucTieu = listDuAnCongViec.filter((item) => {
      const mtctid = item.congViec.mtctid;
      // Nếu không có mtctid hoặc mtctid không nằm trong listTableBSCNEW thì ẩn
      return mtctid && validMucTieuIDs.has(mtctid);
    });
  }

  // Bước 2: filter theo mục tiêu nếu có chọn
  const baseList =
    !targetSelected || targetSelected.id === '0'
      ? filteredByMucTieu
      : filteredByMucTieu.filter((item) => item.congViec.mtctid === targetSelected.id);

  // Bước 3: sort theo listMucTieuOrder (giữ nguyên thứ tự trong listMucTieu)
  if (!listMucTieuOrder || listMucTieuOrder.length === 0) {
    return baseList;
  }

  const withIndex = baseList.map((item) => {
    const mtctid = item.congViec.mtctid || '';
    const idx = listMucTieuOrder.indexOf(mtctid);
    return { item, idx: idx === -1 ? Number.MAX_SAFE_INTEGER : idx };
  });

  withIndex.sort((a, b) => a.idx - b.idx);

  return withIndex.map((x) => x.item);
};

// Summary cho từng tab (theo danh sách công việc hiện tại)
export const calculateSummaryStats = <
  T extends {
    trongKy?: DuAnCongViecTienDoTree;
    congViec?: DuAnCongViec;
  }
>(
  filteredListDuAnCongViec: T[]
): {
  total: number;
  completed: number;
  notStarted: number;
  inProgress: number;
  behindSchedule: number;
  totalBudget: number;
} => {
  const congViecAll = filteredListDuAnCongViec || [];

  const total = congViecAll.length;
  const completed = congViecAll.filter((p) => p.trongKy?.trangThai === 3).length;
  const notStarted = congViecAll.filter((p) => p.trongKy?.trangThai === 1).length;
  const inProgress = congViecAll.filter((p) => p.trongKy?.trangThai === 2).length;
  const behindSchedule = congViecAll.filter((p) => p.trongKy?.trangThai === 4).length;
  const totalBudget = congViecAll.reduce((sum, p) => sum + (p.congViec?.nganSach || 0), 0);

  return {
    total,
    completed,
    notStarted,
    inProgress,
    behindSchedule,
    totalBudget,
  };
};

// Timeline dự án - tiến độ (tính min/max date và list tháng)
export const calculateProjectTimeline = <
  T extends {
    congViec?: { ngayBatDau?: string; ngayKetThuc?: string };
  }
>(
  filteredListDuAnCongViec: T[]
): {
  totalMonth: number;
  months: string[];
  minYear: number;
  minMonth: number;
} | null => {
  const items = filteredListDuAnCongViec || [];
  if (!items || items.length === 0) return null;
  const withDates = items.filter((p) => p.congViec?.ngayBatDau && p.congViec?.ngayKetThuc);
  if (withDates.length === 0) return null;

  const startDates = withDates.map((p) => dayjs(p.congViec!.ngayBatDau!));
  const endDates = withDates.map((p) => dayjs(p.congViec!.ngayKetThuc!));

  // Tự tính min/max vì dayjs.min/max không được bật plugin
  const minDate = startDates.reduce((min, d) => (d.isBefore(min) ? d : min), startDates[0]);
  const maxDateOriginal = endDates.reduce((max, d) => (d.isAfter(max) ? d : max), endDates[0]);

  if (!minDate || !maxDateOriginal) return null;

  const maxDate = maxDateOriginal.add(1, 'month');

  const minYear = minDate.year();
  const maxYear = maxDate.year();
  const minMonth = minDate.month() + 1; // 1-12
  const maxMonth = maxDate.month() + 1;

  const totalMonth =
    minYear === maxYear ? maxDate.diff(minDate, 'month') + 1 : (maxYear - minYear) * 12 + (maxMonth - minMonth) + 1;

  const months: string[] = [];
  for (let i = 0; i < totalMonth; i++) {
    const current = minDate.add(i, 'month');
    months.push(`${current.month() + 1}/${current.year()}`);
  }

  return {
    totalMonth,
    months,
    minYear,
    minMonth,
  };
};



/**
 * Tách count ra khỏi name (format: "text(count)" -> { name: "text", count: "count" })
 */
export const extractCountFromName = (name: string): { name: string; count?: string } => {
  const trimmed = String(name || '').trim();
  const nameMatch = trimmed.match(/^(.+?)\s*\((\d+)\)\s*$/);
  if (nameMatch) {
    return {
      name: nameMatch[1].trim(),
      count: nameMatch[2],
    };
  }
  return { name: trimmed };
};

/** Chuẩn hóa tên mục tiêu dạng cây (cha > con), giữ segment cuối khi truncate UI. */
export const splitObjectivePathSegments = (name: string): { prefix: string; tail: string } => {
  const normalized = String(name || '')
    .replace(/\s*[\r\n]+\s*/g, ' ')
    .trim();
  const parts = normalized.split(/\s*>\s*/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) return { prefix: '', tail: normalized };
  return {
    prefix: parts.slice(0, -1).join(' > '),
    tail: parts[parts.length - 1],
  };
};

export const formatObjectiveFullLabel = (displayName: string, displayCount?: string): string => {
  const normalized = String(displayName || '')
    .replace(/\s*[\r\n]+\s*/g, ' ')
    .trim();
  const count = String(displayCount || '').trim();
  return count ? `${normalized} (${count})` : normalized;
};

/** Tooltip: giữ xuống dòng gốc từ BE. */
export const formatObjectiveTooltipLabel = (displayName: string, displayCount?: string): string => {
  const text = String(displayName || '').trim();
  const count = String(displayCount || '').trim();
  return count ? `${text} (${count})` : text;
};

export type TargetSelectObjectiveItem = {
  key: string;
  id: string;
  name: string;
  count?: string;
};

export type TargetSelectPerspectiveGroup = {
  key: string;
  perspective: string;
  totalObjectives: number;
  phuongdienMauChu?: string;
  phuongdienMauNen?: string;
  phuongdienMauSpan?: string;
  objectives: TargetSelectObjectiveItem[];
};

// /**
//  * Convert Buoc6PhuongDien to MenuSelect format - chia thành 2 phần: phụ trách và không phụ trách
//  * countByMucTieuID: map muctieuID -> số dự án thực tế (ưu tiên hơn count trong tên từ BE)
//  */
export const convertBSCToMenuSelect = (
  listTableBSCNEW: Buoc6PhuongDien[],
  listMucTieuPhuTrachIDs: string[],
  duan?: DuAnCongViecTree | DuAnCongViecTreeSession,
  countByMucTieuID?: Record<string, number>
): {
  phuTrach: TargetSelectPerspectiveGroup[];
  khongPhuTrach: TargetSelectPerspectiveGroup[];
} => {
  const phuTrach: TargetSelectPerspectiveGroup[] = [];
  const khongPhuTrach: TargetSelectPerspectiveGroup[] = [];

  // Gộp theo phuongdienID để tránh double phương diện / mục tiêu (vd. merge viecPhuTrach + viecDuocGiao)
  const mergedByPhuongDien = new Map<
    string,
    {
      phuongdienName: string;
      phuongdienMauChu?: string;
      phuongdienMauNen?: string;
      phuongdienMauSpan?: string;
      listMucTieu: Array<{ muctieuID: string; muctieuName: string }>;
    }
  >();
  listTableBSCNEW.forEach((pd) => {
    const id = pd.phuongdienID || pd.phuongdienName || '';
    const name = pd.phuongdienName || '';
    const existing = mergedByPhuongDien.get(id);
    const list = pd.listMucTieu || [];
    if (!existing) {
      const byMucTieu = new Map<string, string>();
      list.forEach((m) => {
        if (m.muctieuID) byMucTieu.set(m.muctieuID, m.muctieuName || '');
      });
      mergedByPhuongDien.set(id, {
        phuongdienName: name,
        phuongdienMauChu: pd.phuongdienMauChu,
        phuongdienMauNen: pd.phuongdienMauNen,
        phuongdienMauSpan: pd.phuongdienMauSpan,
        listMucTieu: Array.from(byMucTieu.entries()).map(([muctieuID, muctieuName]) => ({ muctieuID, muctieuName })),
      });
    } else {
      const byMucTieu = new Map<string, string>();
      existing.listMucTieu.forEach((m) => byMucTieu.set(m.muctieuID, m.muctieuName || ''));
      list.forEach((m) => {
        if (m.muctieuID) byMucTieu.set(m.muctieuID, m.muctieuName || '');
      });
      mergedByPhuongDien.set(id, {
        phuongdienName: name,
        phuongdienMauChu: existing.phuongdienMauChu,
        phuongdienMauNen: existing.phuongdienMauNen,
        phuongdienMauSpan: existing.phuongdienMauSpan,
        listMucTieu: Array.from(byMucTieu.entries()).map(([muctieuID, muctieuName]) => ({ muctieuID, muctieuName })),
      });
    }
  });
  const mergedList = Array.from(mergedByPhuongDien.entries()).map(([phuongdienID, v]) => ({
    phuongdienID,
    phuongdienName: v.phuongdienName,
    phuongdienMauChu: v.phuongdienMauChu,
    phuongdienMauNen: v.phuongdienMauNen,
    phuongdienMauSpan: v.phuongdienMauSpan,
    listMucTieu: v.listMucTieu,
  }));

  mergedList.forEach((phuongDien, index) => {
    const allMucTieu = phuongDien.listMucTieu || [];
    const seenPhuTrach = new Set<string>();
    const seenKhongPhuTrach = new Set<string>();
    const mucTieuPhuTrach = allMucTieu.filter((mucTieu) => {
      if (!listMucTieuPhuTrachIDs.includes(mucTieu.muctieuID)) return false;
      if (seenPhuTrach.has(mucTieu.muctieuID)) return false;
      seenPhuTrach.add(mucTieu.muctieuID);
      return true;
    });
    const mucTieuKhongPhuTrach = allMucTieu.filter((mucTieu) => {
      if (listMucTieuPhuTrachIDs.includes(mucTieu.muctieuID)) return false;
      if (seenKhongPhuTrach.has(mucTieu.muctieuID)) return false;
      seenKhongPhuTrach.add(mucTieu.muctieuID);
      return true;
    });

    if (mucTieuPhuTrach.length > 0) {
      const objectivesPhuTrach = mucTieuPhuTrach.map((mucTieu, idx) => {
        const { name, count: countFromName } = extractCountFromName(mucTieu.muctieuName);
        const mapped =
          countByMucTieuID != null && mucTieu.muctieuID in countByMucTieuID
            ? countByMucTieuID[mucTieu.muctieuID]
            : undefined;
        const count =
          mapped != null && mapped > 0
            ? String(mapped)
            : countFromName || (mapped === 0 ? undefined : countFromName);
        return {
          key: `${index}-pt-${idx}`,
          id: mucTieu.muctieuID,
          name,
          count,
        };
      });

      phuTrach.push({
        key: `pt-${index + 1}`,
        perspective: phuongDien.phuongdienName,
        totalObjectives: objectivesPhuTrach.length,
        phuongdienMauChu: phuongDien.phuongdienMauChu,
        phuongdienMauNen: phuongDien.phuongdienMauNen,
        phuongdienMauSpan: phuongDien.phuongdienMauSpan,
        objectives: objectivesPhuTrach,
      });
    }

    if (mucTieuKhongPhuTrach.length > 0) {
      const objectivesKhongPhuTrach = mucTieuKhongPhuTrach.map((mucTieu, idx) => {
        const { name, count: countFromName } = extractCountFromName(mucTieu.muctieuName);
        const mapped =
          countByMucTieuID != null && mucTieu.muctieuID in countByMucTieuID
            ? countByMucTieuID[mucTieu.muctieuID]
            : undefined;
        const count =
          mapped != null && mapped > 0
            ? String(mapped)
            : countFromName || (mapped === 0 ? undefined : countFromName);
        return {
          key: `${index}-kpt-${idx}`,
          id: mucTieu.muctieuID,
          name,
          count,
        };
      });

      khongPhuTrach.push({
        key: `kpt-${index + 1}`,
        perspective: phuongDien.phuongdienName,
        totalObjectives: objectivesKhongPhuTrach.length,
        phuongdienMauChu: phuongDien.phuongdienMauChu,
        phuongdienMauNen: phuongDien.phuongdienMauNen,
        phuongdienMauSpan: phuongDien.phuongdienMauSpan,
        objectives: objectivesKhongPhuTrach,
      });
    }
  });
  return { phuTrach, khongPhuTrach };
};

/**
 * Lấy tất cả id mục tiêu có trong listTableBSCNEW (phụ trách + không phụ trách) để khi chọn "Tất cả" chỉ load dự án thuộc các mục tiêu này.
 */
export const getAllMucTieuIdsFromBSC = (listTableBSCNEW: Buoc6PhuongDien[]): string[] => {
  const set = new Set<string>();
  (listTableBSCNEW || []).forEach((pd) => {
    (pd.listMucTieu || []).forEach((m) => {
      if (m.muctieuID) set.add(m.muctieuID);
    });
  });
  return Array.from(set);
};

/**
 * Parse params from URL: [idbophan, idmucTieu, activeTab, idduan, dateRangeUrl, hienThi, idCongViec?]
 * Dùng làm single source cho identityKey / filterKey và tham số API load.
 */
export const parseCongViecDuAnParams = (params: string[] | string | undefined) => {
  const arr = Array.isArray(params) ? params : [];
  const idBoPhan = arr[0] || '0';
  const idMucTieu = arr[1] || '0';
  const activeTab = tabCharToNum(arr[2] || '1');
  const idDuAn = arr[3] || 'undefined';
  const dateRangeFromUrl = arr[4] || null;
  const kieuTrinhBay = arr[5] || '1';
  const idCongViec = arr[6];
  const dateRange = parseDateRangeFromUrl(dateRangeFromUrl);

  return {
    idBoPhan,
    idMucTieu,
    activeTab,
    tabSegment: getTabNumToChar(activeTab),
    idDuAn,
    dateRangeFromUrl,
    dateRange,
    dateChoice: dateRange ?? '',
    kieuTrinhBay,
    idCongViec,
    identityKey: [idBoPhan, idMucTieu, activeTab, idDuAn].join('/'),
    filterKey: `${dateRangeFromUrl ?? ''}/${kieuTrinhBay}`,
  };
};

/** Segment URL hiện tại (ưu tiên pathname khi router.query chưa kịp sync). */
export const getLiveCongViecDuAnPathParams = (): string[] => {
  if (typeof window === 'undefined') return [];
  return window.location.pathname.replace(/^\/cong-viec-du-an\//, '').split('/').filter(Boolean);
};

export const parseLiveCongViecDuAnRoute = (routerParams?: string[] | string) => {
  const live = getLiveCongViecDuAnPathParams();
  const fallback = Array.isArray(routerParams) ? routerParams : [];
  return parseCongViecDuAnParams(live.length > 0 ? live : fallback);
};

// [FIX đổi tab] Kiểm tra URL còn đúng tab — loadDuAn cũ hoàn tất sau khi user đổi tab thì bỏ qua.
export const isCongViecDuAnRouteTab = (activeTab: string): boolean => {
  return parseLiveCongViecDuAnRoute().activeTab === tabCharToNum(activeTab);
};


export { formatDateRangeForUrl } from '@/components/common/ActionPlanV2/core/date';

/**
 * Parse dateRange from URL format "DD-MM-YYYY-DD-MM-YYYY" to "DD/MM/YYYY - DD/MM/YYYY"
 */
export const parseDateRangeFromUrl = (dateRangeStr: string | null): string | null => {
  if (!dateRangeStr || dateRangeStr === 'nulldate') {
    return null;
  }
  // Format từ URL: "DD-MM-YYYY-DD-MM-YYYY" (ví dụ: "30-11-2025-18-12-2025")
  // Cần convert thành: "DD/MM/YYYY - DD/MM/YYYY" (ví dụ: "30/11/2025 - 18/12/2025")
  if (dateRangeStr.includes(' - ') && dateRangeStr.includes('/')) {
    return dateRangeStr;
  }
  const parts = dateRangeStr.split('-');
  if (parts.length === 6) {
    const [d1, m1, y1, d2, m2, y2] = parts;
    return `${d1}/${m1}/${y1} - ${d2}/${m2}/${y2}`;
  }
  return null;
};



export interface kiemTraViTriCongViecParams {
  /** FULL listCongViecDuAnCheck từ API (bao gồm cả node ẩn). */
  baseCheckList: CongViecDuAnCheck[];
  /** Tree records từ TableViewRef.getTableData() (TableRecord-like). */
  tableRecords: TableRecord[];
  /** Root parent id cho các node top-level khi record không có parentID. */
  rootParentId: string;
  /** Tree DuAnCongViecTree[] sẽ được apply viTri (mutate in-place). */
  listCongViecDuAn: DuAnCongViecTree[];
  /** Nếu true: set hasChange cho check item khi viTri đổi hoặc temp. */
  markCheckHasChange?: boolean;
  /**
   * true (mặc định khi lưu dự án): node không có trên bảng giữ viTri từ baseCheckList,
   * chỉ cập nhật viTri node user đang thấy khi khác base.
   */
  preserveHiddenFromBase?: boolean;
}

const collectVisibleTableRecordIds = (tableRecords: TableRecord[]): Set<string> => {
  const ids = new Set<string>();
  const walk = (items: TableRecord[]) => {
    (items || []).forEach((r) => {
      if (r.isPlaceholder) {
        if (Array.isArray(r.childs) && r.childs.length > 0) walk(r.childs as TableRecord[]);
        return;
      }
      const id = String(r.congViec?.id ?? r.key ?? '');
      if (id) ids.add(id);
      if (Array.isArray(r.childs) && r.childs.length > 0) walk(r.childs as TableRecord[]);
    });
  };
  walk(Array.isArray(tableRecords) ? tableRecords as TableRecord[] : []);
  return ids;
};

const viTriValuesEqual = (a: unknown, b: unknown): boolean => {
  const na = typeof a === 'number' && Number.isFinite(a) ? a : null;
  const nb = typeof b === 'number' && Number.isFinite(b) ? b : null;
  return na === nb;
};

const viTriPositionChanged = (prev: unknown, next: unknown): boolean =>
  !viTriValuesEqual(prev, next);

/** Merge FULL listCongViecDuAnCheck (API) + node đang hiển thị trên bảng. */
export const mergeFullListCongViecDuAnCheck = (
  baseCheckList: CongViecDuAnCheck[],
  tableRecords: TableRecord[],
  rootParentId: string
): CongViecDuAnCheck[] => {
  const byId = new Map<string, CongViecDuAnCheck>();
  (Array.isArray(baseCheckList) ? baseCheckList : []).forEach((it) => {
    const id = String(it?.id ?? '');
    if (!id) return;
    byId.set(id, { ...it, id });
  });

  const walk = (items: TableRecord[], parentFromWalk?: string) => {
    (items || []).forEach((r) => {
      if (r?.isPlaceholder) return;
      const id = String(r?.congViec?.id ?? r?.key ?? '');
      if (!id) return;
      const prev = byId.get(id);
      // ParentID ưu tiên: cha trong cây UI → record load → base check (neo NV thiếu cha UI) → root.
      const parentID =
        parentFromWalk !== undefined
          ? String(parentFromWalk)
          : String(r?.congViec?.parentID ?? '').trim() ||
            String(prev?.parentID ?? '').trim() ||
            String(rootParentId ?? '');
      const next: CongViecDuAnCheck = {
        id,
        parentID,
        duanID: String(r?.congViec?.duAnID ?? prev?.duanID ?? ''),
        nganSach: Number(r?.congViec?.nganSach ?? 0),
        tiTrong: (r?.congViec?.tiTrong ?? null) as number | null,
        tiTrongCal: (r?.congViec?.tiTrongCal ?? null) as number | null,
        tiTrongV2: (r?.congViec?.tiTrongV2 ?? null) as number | null,
        tiTrongCalV2: (r?.congViec?.tiTrongCalV2 ?? null) as number | null,
        ngayBatDau: (r?.congViec?.ngayBatDau ?? null) as Date | null,
        ngayKetThuc: (r?.congViec?.ngayKetThuc ?? null) as Date | null,
        viTri: (r?.congViec?.viTri ?? null) as number | null,
      };
      byId.set(id, prev ? ({ ...prev, ...next } as CongViecDuAnCheck) : next);
      if (Array.isArray(r?.childs) && r?.childs.length > 0) {
        walk(r?.childs as TableRecord[], id);
      }
    });
  };
  walk(Array.isArray(tableRecords) ? tableRecords : []);
  return Array.from(byId.values());
};

/**
 * Merge listCongViecDuAnCheck (FULL) với tableRecords, sync viTri, apply vào listCongViecDuAn.
 */
export const kiemTraViTriCongViec = (
  params: kiemTraViTriCongViecParams
): { listCongViecDuAnCheckToSave: CongViecDuAnCheck[]; viTriById: Map<string, number> } => {
  const {
    baseCheckList,
    tableRecords,
    rootParentId,
    listCongViecDuAn,
    markCheckHasChange = false,
    preserveHiddenFromBase = false,
  } = params;

  const baseById = new Map<string, CongViecDuAnCheck>();
  (Array.isArray(baseCheckList) ? baseCheckList : []).forEach((it) => {
    const id = String(it?.id ?? '');
    if (id) baseById.set(id, { ...it, id });
  });

  let listCongViecDuAnCheckToSave = mergeFullListCongViecDuAnCheck(
    baseCheckList,
    tableRecords,
    rootParentId
  );
  const viTriById = new Map<string, number>();

  if (preserveHiddenFromBase) {
    const visibleIds = collectVisibleTableRecordIds(tableRecords);
    listCongViecDuAnCheckToSave = listCongViecDuAnCheckToSave.map((it) => {
      const id = String(it?.id ?? '');
      if (!id) return it;
      const base = baseById.get(id);
      const isTemp = id.startsWith('temp');
      const isVisible = visibleIds.has(id);

      if (!isVisible && base) {
        const kept = { ...it, viTri: base.viTri };
        if (markCheckHasChange) kept.hasChange = false;
        return kept as CongViecDuAnCheck;
      }

      const mergedViTri = it?.viTri;
      const viTriChanged = isTemp || (base ? !viTriValuesEqual(mergedViTri, base.viTri) : true);
      if (viTriChanged && typeof mergedViTri === 'number' && Number.isFinite(mergedViTri)) {
        viTriById.set(id, mergedViTri);
      }
      if (markCheckHasChange) {
        return { ...it, hasChange: isTemp || viTriChanged } as CongViecDuAnCheck;
      }
      return it;
    });
  } else {
    const byParentId = new Map<string, CongViecDuAnCheck[]>();
    for (const item of listCongViecDuAnCheckToSave) {
      const id = String(item?.id ?? '');
      if (!id) continue;
      const parentID = String(item?.parentID ?? '');
      const arr = byParentId.get(parentID);
      if (arr) arr.push(item);
      else byParentId.set(parentID, [item]);
    }

    for (const [, items] of byParentId.entries()) {
      const sorted = [...(items || [])].sort((a, b) => {
        const av = typeof a.viTri === 'number' && Number.isFinite(a.viTri) ? a.viTri : Number.POSITIVE_INFINITY;
        const bv = typeof b.viTri === 'number' && Number.isFinite(b.viTri) ? b.viTri : Number.POSITIVE_INFINITY;
        if (av !== bv) return av - bv;
        return String(a.id).localeCompare(String(b.id));
      });
      sorted.forEach((it, idx) => {
        viTriById.set(String(it.id), idx);
      });
    }

    if (markCheckHasChange) {
      listCongViecDuAnCheckToSave = listCongViecDuAnCheckToSave.map((it) => {
        const id = String(it?.id ?? '');
        const normalized = viTriById.get(id);
        if (normalized === undefined) return it;
        const isTemp = id.startsWith('temp');
        const prevViTri = it?.viTri;
        const changed = isTemp || prevViTri !== normalized;
        return { ...it, viTri: normalized, hasChange: changed };
      });
    }
  }

  const applyToTree = (nodes: DuAnCongViecTree[]) => {
    (nodes || []).forEach((n) => {
      const id = n?.congViec?.id != null ? String(n.congViec.id) : '';
      const nextViTri = id ? viTriById.get(id) : undefined;
      if (nextViTri !== undefined) {
        const prevViTri = n?.congViec?.viTri;
        n.congViec.viTri = nextViTri as number;
        const isTemp = id.startsWith('temp');
        if (isTemp || prevViTri !== nextViTri) {
          n.hasChange = true;
        }
      }
      if (Array.isArray(n?.childs) && n?.childs.length > 0) applyToTree(n?.childs as DuAnCongViecTree[]);
    });
  };
  applyToTree(listCongViecDuAn);

  return { listCongViecDuAnCheckToSave, viTriById };
};

export const parseDateText = (value?: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value, ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD'], true);
  if (parsed.isValid()) return parsed;
  const isoParsed = dayjs(value);
  return isoParsed.isValid() ? isoParsed : null;
};

/** Node dự án rút gọn lưu sessionStorage (congViec + childs + roleItemTree). */
export type DuAnCongViecTreeSession = Pick<DuAnCongViecTree, 'congViec' | 'roleItemTree'> & {
  childs?: Array<{ congViec?: DuAnCongViecTree['congViec'] }>;
};

export interface FilterProjectsResult {
  listDuAn: DuAnCongViecTree[];
  idCongViecGanNhat: string[];
  selected: DuAnCongViecTree | null;
  resetToDefaultTarget: boolean;
}

/** Lọc danh sách dự án theo mục tiêu hiện tại và lấy idCongViecGanNhat từ childs của dự án */
export const filterProjectsByTarget = (
  listDuAn: DuAnCongViecTree[] | undefined,
  targetId: string,
  currentDuanId?: string,
  allowedMucTieuIdsWhenAll?: string[]
): FilterProjectsResult => {
  let projectFiltered: DuAnCongViecTree[] = [];
  let idCongViecGanNhat: string[] = [];
  const list = listDuAn || [];

  if (targetId === '-1') {
    projectFiltered = [];
  } else if (targetId.includes(',')) {
    const set = new Set(targetId.split(',').map((x) => x.trim()).filter(Boolean));
    projectFiltered = list.filter((item) => {
      const mtctid = String(item.congViec?.mtctid ?? '');
      return mtctid && set.has(mtctid);
    });
  } else if (targetId === '0') {
    if (allowedMucTieuIdsWhenAll && allowedMucTieuIdsWhenAll.length > 0) {
      const set = new Set(allowedMucTieuIdsWhenAll);
      projectFiltered = list.filter((item) => {
        const mtctid = item.congViec?.mtctid;
        return mtctid != null && set.has(String(mtctid));
      });
    } else {
      projectFiltered = list;
    }
  } else {
    projectFiltered = list.filter((item) => item.congViec?.mtctid === targetId);
  }

  if (currentDuanId && currentDuanId !== 'undefined') {
    const duan = list.find((item) => item.congViec?.id === currentDuanId);
    const duanChilds = Array.isArray(duan?.childs) ? duan.childs : [];
    if (duanChilds.length > 0) {
      idCongViecGanNhat = duanChilds.map((child) => child?.congViec?.id).filter(Boolean) as string[];
    } else {
      idCongViecGanNhat = list
        .filter((item) => item.congViec?.parentID === currentDuanId)
        .map((item) => item.congViec?.id)
        .filter(Boolean) as string[];
    }
  } else {
    idCongViecGanNhat = list.map((item) => item.congViec?.id).filter(Boolean) as string[];
  }

  return {
    listDuAn: projectFiltered,
    idCongViecGanNhat: idCongViecGanNhat || [],
    selected: projectFiltered[0] || null,
    resetToDefaultTarget: false,
  };
};

/** Lấy mảng tiến độ/chỉ tiêu đã chia từ map do TableView (sau ChiaChiTieuModal). */
export const listChiTieuFromDicMap = (
  dicTienDosByCongViecId: Record<string, Record<string, DuAnCongViecChiTieuItem[]>> | undefined,
  congViecId: string
): DuAnCongViecChiTieuItem[] | undefined => {
  if (!dicTienDosByCongViecId || !congViecId) return undefined;
  const nested = dicTienDosByCongViecId[congViecId];
  if (!nested || typeof nested !== 'object') return undefined;
  const direct = nested[congViecId];
  if (Array.isArray(direct)) return direct;
  const first = Object.values(nested).find((v) => Array.isArray(v));
  return Array.isArray(first) ? first : undefined;
};

/**
 * Convert TableRecord[] to DuAnCongViecTree[] (remove UI fields and map children → childs)
 */
export const sanitizeRecord = (
  record: TableRecord,
  dicTienDosByCongViecId?: Record<string, Record<string, DuAnCongViecChiTieuItem[]>>
): DuAnCongViecTree => {
  const treeData = { ...record };
  const childRecords: TableRecord[] =
    (Array.isArray(record.childs) && record.childs.length > 0
      ? (record.childs as TableRecord[])
      : (record.childs as TableRecord[] | undefined)) || [];
  const childs = childRecords.map((child) => sanitizeRecord(child, dicTienDosByCongViecId));

  // FE cascade: node hoặc bất kỳ descendant nào thay đổi → BE không skip subtree này.
  const ownChanged =
    treeData.hasChange === true ||
    String(treeData.congViec?.id ?? '').trim().startsWith('temp') ||
    String(treeData.idTemp ?? '').trim().startsWith('temp');
  const hasChangeInTree = ownChanged || childs.some((c) => c.hasChangeInTree === true);

  const defaultTienDo: DuAnCongViecTienDoTree = {
    icon: treeData.trongKy?.icon || '',
    titleIcon: treeData.trongKy?.titleIcon || '',
    giaiDoan: treeData.trongKy?.giaiDoan || '',
    tienDoHomNay: treeData.trongKy?.tienDoHomNay || 0,
    hoanThanh: treeData.trongKy?.hoanThanh || 0,
    trangThai: treeData.trongKy?.trangThai || 1,
  };
  const newCongViec = {
    ...(treeData.congViec || {}),
    hasChange: treeData.hasChange === true,
    hasChilds: childs.length > 0,
    Kanbans: treeData.congViec?.kanbans || treeData.congViec?.kanbans || [],
    ngayBatDau: treeData.congViec?.ngayBatDau,
    ngayKetThuc: treeData.congViec?.ngayKetThuc,
  } as DuAnCongViec;
  // BE gán NgayTao/NgayCapNhat — không gửi từ FE (tránh 400 bind DateTime).
  delete (newCongViec as { ngayTao?: unknown }).ngayTao;
  delete (newCongViec as { ngayCapNhat?: unknown }).ngayCapNhat;

  const idCv = String(treeData.congViec?.id ?? '').trim();
  const listChiTieuFromModal = listChiTieuFromDicMap(dicTienDosByCongViecId, idCv);
  // Đổi tần suất chưa chia lại → không gửi listChiTieu/kỳ cũ (BE UpdateTienDoDuAn theo tanSuat mới).
  const tanSuatChanged =
    treeData.initialTanSuat !== undefined &&
    treeData.initialTanSuat !== null &&
    Number(treeData.congViec?.tanSuat) !== Number(treeData.initialTanSuat);
  const rawListChiTieu = tanSuatChanged
    ? []
    : listChiTieuFromModal !== undefined
      ? listChiTieuFromModal
      : (treeData.listChiTieu ?? []);
  const listChiTieu = normalizeListChiTieuNgayForApi(rawListChiTieu);

  const defaultRoleItemTree: RoleItemTree = {
    isNguoiTao: false,
    isPhuTrach: false,
    isHoTro: false,
    hasChild: childs.length > 0,
    isEdit: true,
    hasAddSub: true,
    isRoot: false,
    isView: true,
    isDelete: false,
  };
  const roleItemTree: RoleItemTree = {
    ...defaultRoleItemTree,
    ...(treeData.roleItemTree && typeof treeData.roleItemTree === 'object' ? treeData.roleItemTree : {}),
  };

  return {
    ...treeData,
    chiTieu: treeData.congViec?.chiTieu || '',
    congViec: newCongViec as DuAnCongViecDTO,
    param: treeData.param || '',
    thoiGian: treeData.thoiGian != null && treeData.thoiGian !== '' ? treeData.thoiGian : '',
    tenTanSuat: treeData.tenTanSuat || 'Ngày',
    tenHinhThucTinh: treeData.tenHinhThucTinh || 'Báo cáo nhập tay',
    roleItemTree,
    trongKy: defaultTienDo,
    luyke: treeData.luyke || defaultTienDo,
    hasChild: childs.length > 0,
    countChilds: childs.length,
    childs,
    hasChangeInTree,
    idTemp: treeData.idTemp || newCongViec?.id || 'temp',
    listChiTieu,
    /** Giữ columnValues trên tree — BE upsert trong editcongviecs. */
    columnValues: Array.isArray(treeData.columnValues) ? treeData.columnValues : [],
  } as DuAnCongViecTree;
};

/**
 * Convert TableRecord[] to DuAnCongViecTree[]
 */
export const convertTableRecordsToDuAnCongViecTree = (
  records: TableRecord[],
  dicTienDosByCongViecId?: Record<string, Record<string, DuAnCongViecChiTieuItem[]>>
): DuAnCongViecTree[] =>
  records
    .filter((record) => !record.isPlaceholder)
    .map((record) => sanitizeRecord(record, dicTienDosByCongViecId));

/** Cascade hasChange → hasChangeInTree (bottom-up). */
function cascadeHasChangeInTree(nodes: DuAnCongViecTree[]): boolean {
  let anyChanged = false;
  nodes.forEach((n) => {
    const childChanged = n.childs?.length ? cascadeHasChangeInTree(n.childs) : false;
    n.hasChangeInTree = n.hasChange === true || childChanged;
    anyChanged = anyChanged || n.hasChangeInTree;
  });
  return anyChanged;
}

/**
 * Gắn hasChange / hasChangeInTree theo "thay đổi thật" của user từ source tree (build trước khi
 * re-normalize tỉ trọng) lên target tree gửi lưu. Loại bỏ mark hệ thống (nếu còn) để BE chỉ
 * update đúng node user sửa — edit cha không kéo hasChange=true xuống mọi con.
 */
export const syncHasChangeInTreeToSaveTree = (
  target: DuAnCongViecTree[],
  source: DuAnCongViecTree[]
): DuAnCongViecTree[] => {
  if (!Array.isArray(source) || source.length === 0) return target;
  cascadeHasChangeInTree(source);
  const changeInTreeIds = new Set<string>();
  const hasChangeIds = new Set<string>();
  const collect = (nodes: DuAnCongViecTree[]) =>
    nodes.forEach((n) => {
      const id = String(n.congViec?.id ?? n.idTemp ?? '');
      if (id && n.hasChangeInTree === true) changeInTreeIds.add(id);
      if (id && n.hasChange === true) hasChangeIds.add(id);
      if (n.childs?.length) collect(n.childs);
    });
  collect(source);
  const patch = (nodes: DuAnCongViecTree[]): DuAnCongViecTree[] =>
    nodes.map((n) => {
      const id = String(n.congViec?.id ?? n.idTemp ?? '');
      const next = {
        ...n,
        hasChange: hasChangeIds.has(id),
        hasChangeInTree: changeInTreeIds.has(id),
      };
      if (n.childs?.length) next.childs = patch(n.childs);
      return next;
    });
  return patch(target);
};

export const collectItemsWithIdTemp = (items: DuAnCongViecTree[], idTempMap: Map<string, DuAnCongViecTree>) => {
  items.forEach((item) => {
    const idTemp = item.idTemp;
    if (idTemp && idTemp.startsWith('temp')) {
      idTempMap.set(idTemp, item);
    }
    if (item.childs && item.childs.length > 0) {
      collectItemsWithIdTemp(item.childs, idTempMap);
    }
  });
};

/** Merge patch Kanban/API vào cây hiện tại — chỉ congViec/trongKy/luyke + vài cờ. */
export const mergeCongViecDataByUpdatedTrees = (
  prev: DuAnCongViecTree[],
  updatedTrees: DuAnCongViecTree[]
): DuAnCongViecTree[] => {
  if (!Array.isArray(updatedTrees) || updatedTrees.length === 0) return prev;
  const updatedById = new Map<string, DuAnCongViecTree>();
  const flattenForMerge = (items: DuAnCongViecTree[]): DuAnCongViecTree[] => {
    const out: DuAnCongViecTree[] = [];
    items.forEach((it) => {
      out.push(it);
      if (Array.isArray(it.childs) && it.childs.length > 0) out.push(...flattenForMerge(it.childs));
    });
    return out;
  };
  flattenForMerge(updatedTrees).forEach((item) => {
    const id = String(item?.congViec?.id ?? '').trim();
    if (id) updatedById.set(id, item);
  });
  if (updatedById.size === 0) return prev;

  const mergeNodes = (nodes: DuAnCongViecTree[]): DuAnCongViecTree[] =>
    nodes.map((node) => {
      const id = String(node?.congViec?.id ?? '').trim();
      const patch = id ? updatedById.get(id) : undefined;
      let merged: DuAnCongViecTree = node;
      if (patch) {
        merged = {
          ...node,
          congViec: {
            ...(node?.congViec || {}),
            ...(patch?.congViec || {}),
          },
          trongKy: {
            ...(node?.trongKy || {}),
            ...(patch?.trongKy || {}),
          },
          luyke: {
            ...(node?.luyke || {}),
            ...(patch?.luyke || {}),
          },
        };
        if (patch.hasChange !== undefined) merged = { ...merged, hasChange: patch.hasChange };
        if (patch.idTemp !== undefined && patch.idTemp !== null) merged = { ...merged, idTemp: patch.idTemp };
      }
      const childs = Array.isArray(merged.childs) ? merged.childs : [];
      return { ...merged, childs: mergeNodes(childs) };
    });

  return mergeNodes(prev);
};

/** Kanban thêm card: merge patch + append root mới từ BE (chưa có trong congViecData). */
export const syncCongViecDataFromKanbanEditResponse = (
  prev: DuAnCongViecTree[],
  responseRecords: DuAnCongViecTree[],
  duAnId?: string
): DuAnCongViecTree[] => {
  if (!Array.isArray(responseRecords) || responseRecords.length === 0) return prev;

  const flatten = (items: DuAnCongViecTree[]): DuAnCongViecTree[] => {
    const out: DuAnCongViecTree[] = [];
    items.forEach((it) => {
      out.push(it);
      if (Array.isArray(it.childs) && it.childs.length > 0) out.push(...flatten(it.childs));
    });
    return out;
  };

  const persistedTempIds = new Set<string>();
  flatten(responseRecords).forEach((item) => {
    const idTemp = String(item?.idTemp ?? '').trim();
    if (idTemp.startsWith('temp')) persistedTempIds.add(idTemp);
  });

  let merged = mergeCongViecDataByUpdatedTrees(prev, responseRecords);
  if (persistedTempIds.size > 0) {
    merged = merged.filter((node) => {
      const id = String(node?.congViec?.id ?? '').trim();
      return !(id.startsWith('temp') && persistedTempIds.has(id));
    });
  }

  const collectIds = (nodes: DuAnCongViecTree[], ids = new Set<string>()): Set<string> => {
    nodes.forEach((n) => {
      const id = String(n?.congViec?.id ?? '').trim();
      if (id) ids.add(id);
      if (Array.isArray(n.childs) && n.childs.length > 0) collectIds(n.childs, ids);
    });
    return ids;
  };
  const existingIds = collectIds(merged);

  const newRoots: DuAnCongViecTree[] = [];
  flatten(responseRecords).forEach((item) => {
    const id = String(item?.congViec?.id ?? '').trim();
    if (!id || id.startsWith('temp') || existingIds.has(id)) return;

    const parentId = String(item?.congViec?.parentID ?? '').trim();
    const isRootUnderDuAn =
      !parentId || (duAnId && parentId === duAnId) || !existingIds.has(parentId);

    if (isRootUnderDuAn) {
      newRoots.push(item);
      existingIds.add(id);
    }
  });

  return newRoots.length > 0 ? [...merged, ...newRoots] : merged;
};

export type KanbanTableChangeMap = Map<string, { kanBanColumnID?: string; kanBanViTri: number }>;

export const applyKanbanChangesToTableRecords = <
  T extends {
    congViec?: { id?: string; kanBanColumnID?: string; kanBanViTri?: number };
    childs?: T[];
    hasChange?: boolean;
  },
>(
  tableRecords: T[],
  changes: KanbanTableChangeMap
): T[] => {
  if (changes.size === 0) return tableRecords;

  const patchRecord = (record: T): T => {
    const idCongViec = record.congViec?.id;
    let next = record;

    if (idCongViec && changes.has(idCongViec)) {
      const ch = changes.get(idCongViec)!;
      next = {
        ...record,
        hasChange: true,
        congViec: {
          ...record.congViec,
          kanBanColumnID: ch.kanBanColumnID ?? record.congViec?.kanBanColumnID,
          kanBanViTri: ch.kanBanViTri,
        },
      } as T;
    }

    const childs = record.childs;
    if (childs?.length) {
      const patchedChilds = childs.map(patchRecord);
      const childsChanged = patchedChilds.some((child, index) => child !== childs[index]);
      if (childsChanged) {
        next = { ...next, childs: patchedChilds };
      }
    }

    return next;
  };

  return tableRecords.map(patchRecord);
};

export type ValidateListCongViecDuAnResult = 'ok' | 'no-change' | 'empty-name';

/** Bật/tắt validate tên công việc trước editcongviecs. */
export const CHECK_EMPTY_TEN_CONG_VIEC_BEFORE_SAVE = false;

/** Validate ListCongViecDuAn trước editcongviecs. */
export const validateListCongViecDuAnBeforeSave = (
  items: DuAnCongViecTree[],
  checkEmptyName = CHECK_EMPTY_TEN_CONG_VIEC_BEFORE_SAVE
): ValidateListCongViecDuAnResult => {
  if (!items?.length) return 'no-change';
  let hasChange = false;
  const walk = (nodes: DuAnCongViecTree[]): ValidateListCongViecDuAnResult | null => {
    for (const node of nodes) {
      const id = String(node.congViec?.id ?? '');
      const idTemp = String(node.idTemp ?? '');
      if (node.hasChange === true || id.startsWith('temp') || idTemp.startsWith('temp')) {
        hasChange = true;
      }
      if (checkEmptyName) {
        const name = node.congViec?.tenCongViec;
        if (name == null || String(name).trim() === '') return 'empty-name';
      }
      if (node.childs?.length) {
        const r = walk(node.childs);
        if (r) return r;
      }
    }
    return null;
  };
  const invalid = walk(items);
  if (invalid) return invalid;
  return hasChange ? 'ok' : 'no-change';
};

export const hasTableRecordsNewTasks = (records: TableRecord[]): boolean => {
  for (const record of records) {
    const isTempRecord =
      record?.temp === true ||
      (record?.congViec?.id && String(record.congViec.id).startsWith('temp')) ||
      (record?.key && String(record.key).startsWith('temp'));
    if (isTempRecord) return true;
    if (Array.isArray(record?.childs) && record.childs.length > 0) {
      if (hasTableRecordsNewTasks(record.childs as TableRecord[])) return true;
    }
  }
  return false;
};

/** Có row đã lưu đổi nguoiPhuTrachID (_phuTrachChanged từ columns). */
export const hasTableRecordsPhuTrachChanged = (records: TableRecord[]): boolean => {
  for (const r of records) {
    if (r._phuTrachChanged === true) return true;
    if (r.childs && r.childs.length > 0 && hasTableRecordsPhuTrachChanged(r.childs as TableRecord[])) return true;
  }
  return false;
};

/** Clone preview copy: chỉ giữ node được check hoặc có con được check. */
export const filterCloneCopyCheckedTableRecords = (items: TableRecord[]): TableRecord[] => {
  const result: TableRecord[] = [];
  items.forEach((item) => {
    const childs = Array.isArray(item.childs) ? item.childs : [];
    const newChilds = filterCloneCopyCheckedTableRecords(childs as TableRecord[]);
    const selfChecked = item.isCheck === true;
    const include = selfChecked || newChilds.length > 0;
    if (!include) return;
    const newItem: TableRecord = { ...item, childs: newChilds };
    const childHasChange = newChilds.some((c: TableRecord) => c.hasChange === true);
    newItem.hasChange = selfChecked || item.hasChange === true || childHasChange;
    if (selfChecked && newItem.congViec?.id && !String(newItem.congViec.id).startsWith('temp')) {
      const newId = CommonJs.createTempID();
      newItem.congViec = { ...newItem.congViec, id: newId };
      newItem.key = newId;
    }
    result.push(newItem);
  });
  return result;
};

export type TableViewRefLike = {
  openModalDetailByCongViecId?: (congViecId: string) => void | Promise<void>;
};

export function scheduleOpenTableDetailByCongViecId(
  tableRef: { current: TableViewRefLike | null },
  congViecId: string
) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      void tableRef.current?.openModalDetailByCongViecId?.(congViecId);
    });
  });
}

/** Backend expect filesDinhKem as string; form may store array of URLs. */
export const normalizeFilesDinhKemToString = (v: string[]): string => {
  if (v == null || v.length === 0) return '';
  if (Array.isArray(v)) return v.filter((u: string) => Boolean(String(u).trim())).join(',');
  return String(v).trim();
}



export const clearCongViecDuAnSession = () => {
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
    }
  }
};

export type CongViecDuAnSession = {
  listTableBSCNEW?: Buoc6PhuongDien[];// list tất cả mục tiêu ( phụ trách, không phụ trách)
  listMucTieuPhuTrachIDs?: string[];// list mục tiêu phụ trách để filter
  currentDuAn?: DuAnCongViecTree | DuAnCongViecTreeSession;
  listDuAn?: DuAnCongViecTree[];// tổng dự án 
  idCongViecGanNhat?: string[]; //id công việc gần nhất của curent dự án để có tham số loadCongViecbyDuAn (rule: cần có các công việc dưới cấp dự án) 
  idSelect?: string;  /** Id phòng ban khi lưu session – dùng để chỉ dùng session khi đúng phòng ban */
  fromDuAn?: boolean; // biến xác nhận bấm từ trang du-an
};

/** Rút gọn node cho session: giữ full congViec + roleItemTree, bỏ tiến độ/cây con sâu. */
export function slimDuAnCongViecTreeForSession(
  tree: DuAnCongViecTree | DuAnCongViecTreeSession | null | undefined
): DuAnCongViecTreeSession | undefined {
  if (!tree || typeof tree !== 'object') return undefined;
  const childs = Array.isArray(tree.childs)
    ? tree.childs.map((c) => ({
      congViec: c?.congViec ? { ...c.congViec } : undefined,
    }))
    : undefined;
  return {
    congViec: tree.congViec ? { ...tree.congViec } : ({} as DuAnCongViecTree['congViec']),
    childs,
    roleItemTree: tree.roleItemTree,
  };
}

/**
 * Header dự án (getDashboardDuAn / session) thường thiếu ngày, đơn vị…
 * Gộp mẫu từ loadCongViecByDuAn; chỉ ghi đè identity dự án, không spread project lên seed.
 */
export function enrichDuAnTreeForTable(
  duan?: DuAnCongViecTree | null,
  congViecData?: DuAnCongViecTree[] | null
): DuAnCongViecTree | undefined {
  if (!duan) return undefined;
  const seedCv = congViecData?.[0]?.congViec;
  if (!seedCv) return duan;
  const projectCv = duan.congViec;
  return {
    ...duan,
    congViec: {
      ...seedCv,
      id: projectCv.id,
      duAnID: projectCv.duAnID ?? projectCv.id,
      isDuAn: projectCv.isDuAn ?? true,
      tenCongViec: projectCv.tenCongViec,
      parentID: projectCv.parentID ?? '',
      mtctid: projectCv.mtctid ?? seedCv.mtctid,
    },
    childs: duan.childs?.length ? duan.childs : congViecData ?? duan.childs,
  };
}

export type SetCongViecDuAnSessionInput = CongViecDuAnSession;

function buildSessionJson(input: SetCongViecDuAnSessionInput, minimal = false): string {
  const listDuAn = (input.listDuAn ?? [])
    .map((item) => slimDuAnCongViecTreeForSession(item))
    .filter(Boolean) as DuAnCongViecTreeSession[];
  const currentDuAn = input.currentDuAn ? slimDuAnCongViecTreeForSession(input.currentDuAn) : undefined;

  if (minimal) {
    return JSON.stringify({
      listMucTieuPhuTrachIDs: input.listMucTieuPhuTrachIDs,
      currentDuAn,
      listDuAn,
      idCongViecGanNhat: input.idCongViecGanNhat,
      idSelect: input.idSelect,
      fromDuAn: input.fromDuAn,
    });
  }

  return JSON.stringify({
    listTableBSCNEW: input.listTableBSCNEW,
    listMucTieuPhuTrachIDs: input.listMucTieuPhuTrachIDs,
    currentDuAn,
    listDuAn,
    idCongViecGanNhat: input.idCongViecGanNhat,
    idSelect: input.idSelect,
    fromDuAn: input.fromDuAn,
  });
}

/** Lưu session an toàn — payload gọn, fallback minimal khi vượt quota. */
export function setCongViecDuAnSession(input: SetCongViecDuAnSessionInput): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window.sessionStorage.setItem(SESSION_KEY, buildSessionJson(input, false));
    return true;
  } catch (error) {
    const isQuota =
      error instanceof DOMException &&
      (error.name === 'QuotaExceededError' || error.code === 22);
    if (!isQuota) {
      console.error('Error saving cong-viec-du-an session:', error);
      return false;
    }
    try {
      window.sessionStorage.setItem(SESSION_KEY, buildSessionJson(input, true));
      return true;
    } catch (retryError) {
      console.warn('cong-viec-du-an session skipped (storage quota):', retryError);
      try {
        window.sessionStorage.removeItem(SESSION_KEY);
      } catch {
        /* ignore */
      }
      return false;
    }
  }
}

export const getCongViecDuAnSession = (): CongViecDuAnSession | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as CongViecDuAnSession) : null;
  } catch {
    return null;
  }
};

/**
 * Session hợp lệ khi: cùng phòng ban (idSelect) và idDuAn nằm trong danh sách dự án.
 * Dùng để quyết định load từ session (chỉ loadCongViecByDuAn) hay gọi loadDuAn.
 */
export const isSessionValidForDepartment = (
  session: CongViecDuAnSession | null,
  idSelect: string | undefined,
  idDuAn: string
): boolean => {
  if (!session?.listDuAn?.length || !idDuAn || idDuAn === 'undefined') return false;
  const sameDepartment = (session.idSelect ?? '') === (idSelect ?? '');
  const projectInList = session.listDuAn.some((p) => p.congViec?.id === idDuAn);
  return sameDepartment && projectInList;
};

/** Ưu tiên URL params + filter mục tiêu đang chọn (csv). */
export const resolveMucTieuFilterFromRoute = (
  idMucTieuFromURL: string,
  selectedTargetIdsCsv: string,
  idBoPhan: string,
  idSelect: string
): string => {
  if (idBoPhan !== (idSelect ?? '0')) return '0';
  const urlMucTieu = (idMucTieuFromURL || '0').trim();
  if (!urlMucTieu || urlMucTieu === '0') {
    const csv = selectedTargetIdsCsv.trim();
    if (csv === '-1') return '-1';
    return '0';
  }
  const csv = selectedTargetIdsCsv.trim();
  if (csv === '-1') return '-1';
  return csv || urlMucTieu;
};

/** Sau loadCongViecByDuAn — cập nhật idCongViecGanNhat vào session hiện có. */
export const patchCongViecDuAnSessionAfterCongViecLoad = (
  idCongViecGanNhat: string[],
  idSelect?: string
): void => {
  const parsed = getCongViecDuAnSession();
  if (!parsed) return;
  setCongViecDuAnSession({
    ...parsed,
    idCongViecGanNhat,
    idSelect: idSelect ?? parsed.idSelect,
  });
};

/** idMucTieu URL/filter → id đơn cho API */
export const resolveIdMucTieuForApi = (idMucTieu?: string): string | undefined => {
  const raw = String(idMucTieu ?? '').trim();
  if (!raw || raw === '0' || raw === '-1') return undefined;
  const first = raw.split(',')[0]?.trim();
  return first || undefined;
};

/** CSV filter mục tiêu → segment URL; rỗng (tất cả) → mtctid dự án */
export const resolveIdMucTieuForCongViecDuAnNav = (
  selectedTargetIdsCsv: string | undefined,
  projectMtctid?: string
): string => {
  const csv = String(selectedTargetIdsCsv ?? '').trim();
  if (csv === '-1') return '-1';
  if (csv) return csv;
  return String(projectMtctid ?? '').trim() || '0';
};

export const buildCongViecDuAnUrl = (
  idBoPhan: string, //idSelect từ url hoặc  getRequestHeader có thay đổi
  idMucTieu: string, //idMucTieu từ url hoặc từ select mục tiêu có thay đổi
  tabSegment: string, //tabSegment từ url hoặc từ select tab có thay đổi ( phụ trách, đã giao , hỗ trợ)
  idDuAn: string, //idDuAn từ url hoặc từ select dự án ở (left) có thay đổi
  formattedDateRange: string | null, //dateRange từ url hoặc từ select date range có thay đổi
  hienThi: string //hienThi từ url hoặc từ select hien thi có thay đổi (việc của tôi (1), tổng quan việc toàn cây(2), tổng quan việc của tôi(3))
) => `/cong-viec-du-an/${[idBoPhan || '0', idMucTieu || '0', tabSegment, idDuAn || 'undefined', formattedDateRange, hienThi].join('/')}`;

export const getIdCongViecGanNhatFromDuAnCongViecTree = (
  projectId: string,
  listDuAn: DuAnCongViecTree[],
  session?: CongViecDuAnSession | null
): string[] => {
  const selectedProject =
    listDuAn.find((p) => p.congViec?.id === projectId) ??
    session?.listDuAn?.find((p) => p.congViec?.id === projectId) ??
    (session?.currentDuAn?.congViec?.id === projectId ? session.currentDuAn : undefined);

  if (selectedProject?.childs?.length) {
    return selectedProject.childs.map((child) => child?.congViec?.id).filter(Boolean) as string[];
  }

  const children = listDuAn.filter((item) => item.congViec?.parentID === projectId);
  if (children.length) {
    return children.map((item) => item.congViec?.id).filter(Boolean) as string[];
  }

  return session?.idCongViecGanNhat ?? [];
};


/** Parse ngày công việc (string ISO / Date / DD/MM…) → dayjs đầu ngày, dùng cho cột Thời gian. */
export const parseCongViecDateForRange = (value: unknown): dayjs.Dayjs | null => {
  const ymd = actionPlanCalendarYMD(value as Date | string | null | undefined);
  if (!ymd) return null;
  const d = dayjs(ymd, 'YYYY-MM-DD', true);
  return d.isValid() ? d.startOf('day') : null;
}

/** Số ngày trong khoảng [start, end] (cùng ngày = 1). */
export const dayCountInclusiveRange = (startRaw: unknown, endRaw: unknown): number | null => {
  const start = parseCongViecDateForRange(startRaw);
  const end = parseCongViecDateForRange(endRaw);
  if (!start || !end) return null;
  if (end.isBefore(start, 'day')) return null;
  return end.diff(start, 'day') + 1;
}



/**
 * Convert DuAnCongViecTree[] sang TableRecord[] cho Ant Design Table
 * Chỉ thêm key, childs (map từ childs), và các UI helpers
 * Giữ nguyên tất cả data từ DuAnCongViecTree
 */
export const convertToTableData = (
  tree: DuAnCongViecTree[],
  parentMap?: Map<string, DuAnCongViecTree>
): TableRecord[] => {
  const convertItem = (item: DuAnCongViecTree, parentName?: string): TableRecord => {
    // Convert childs
    const childs =
      item.childs?.map((child) => {
        const childParentName = parentMap?.get(child.congViec.parentID || '')?.congViec.tenCongViec;
        return convertItem(child, childParentName);
      }) || [];

    // Tạo TableRecord từ DuAnCongViecTree, chỉ thêm các field cần thiết
    // Key phải ổn định và unique để React/AntD không phải remount toàn bộ tree mỗi lần render
    const stableKey = item.congViec.treePath || item.congViec.id || CryptoService.generateRandomIV();
    const id = String(item.congViec?.id ?? stableKey ?? '');
    const idTemp = String((item as TableRecord)?.idTemp ?? '');
    const isTemp = id.startsWith('temp') || idTemp.startsWith('temp');

    const tableItem: TableRecord = {
      ...item, // Spread tất cả fields từ DuAnCongViecTree
      key: stableKey, // Required cho Ant Design Table
      parentName: parentName, // Helper field để hiển thị tên parent
      childs: childs,
      // API đôi khi trả hasChange=true cho một số node; UI chỉ coi "unsaved" khi user sửa trên FE.
      // Vì vậy: reset hasChange cho data load, chỉ giữ cho record temp (nháp).
      hasChange: isTemp ? true : false,
      temp: isTemp ? true : false,
      initialTanSuat: item.congViec?.tanSuat,
    };
    // Map childs -> childs cho Ant Design Table
    return tableItem as TableRecord;
  };

  return tree.map((item) => {
    const parentName = parentMap?.get(item.congViec.parentID || '')?.congViec.tenCongViec;
    return convertItem(item, parentName);
  });
};

/** Giá trị tỉ trọng đang hiển thị của một dòng (ưu tiên tiTrong, không thì tiTrongCal). */
export const getRecordDisplayTiTrong = (s: TableRecord): number => {
  const t = s.congViec.tiTrong;
  if (t !== null && t !== undefined && Number.isFinite(Number(t))) return Number(t);
  const cal = s.congViec.tiTrongCal;
  if (cal !== null && cal !== undefined && Number.isFinite(Number(cal))) return Number(cal);
  return 0;
};

/** Tỉ trọng mục tiêu của công việc (ưu tiên tiTrong đã nhập, không thì tiTrongCal) — cùng quy ước khi thêm con / cập nhật anh em. */
export const getCongViecTiTrongTarget = (cv: TableRecord['congViec']): number => {
  if (cv.tiTrong !== null && cv.tiTrong !== undefined && Number.isFinite(Number(cv.tiTrong))) {
    return Number(cv.tiTrong);
  }
  if (cv.tiTrongCal !== null && cv.tiTrongCal !== undefined && Number.isFinite(Number(cv.tiTrongCal))) {
    return Number(cv.tiTrongCal);
  }
  return 0;
};

//TẠO CÔNG VIỆC
export const createNewTask = (
  isChild: boolean = false,
  parentRecord?: TableRecord,
  duan?: DuAnCongViecTree,
  profile?: ProfileUser,
  baseParentRecords?: TableRecord[],
  idDuAn?: string,
  defaultPhuTrachId?: string | null
): TableRecord => {
  const tempId = CommonJs.createTempID();
  const level = parentRecord ? (parentRecord.congViec.level || 0) + 1 : 1;

  // Kế thừa field từ duan; với công việc con, ngày phải theo cha trên bảng (kể cả chưa lưu), không theo snapshot dự án
  let baseParentCongViec: DuAnCongViecDTO;
  if (duan) {
    baseParentCongViec = duan.congViec;
  } else {
    baseParentCongViec = {} as DuAnCongViecDTO;
  }
  if (isChild && parentRecord?.congViec) {
    const p = parentRecord.congViec;
    baseParentCongViec = {
      ...baseParentCongViec,
      ngayBatDau: p.ngayBatDau ?? baseParentCongViec.ngayBatDau ?? dayjs().toDate(),
      ngayKetThuc: p.ngayKetThuc ?? baseParentCongViec.ngayKetThuc ?? dayjs().toDate(),
    };
  }

  // Tính tỉ trọng cho công việc mới
  // Nếu có parent, tính tỉ trọng dựa trên tiTrongCal của parent và số lượng siblings
  let tiTrongCal = 100;
  if (parentRecord && baseParentRecords) {
    const parentTarget = getCongViecTiTrongTarget(parentRecord.congViec);
    const parentTiTrongCal = parentTarget > 0 ? parentTarget : (parentRecord.congViec.tiTrongCal ?? 100);

    // Tìm tất cả siblings (các childs cùng parent) trong tree
    const findSiblingsLocal = (records: TableRecord[], pId: string): TableRecord[] => {
      const siblings: TableRecord[] = [];
      const traverse = (list: TableRecord[]) => {
        for (const item of list) {
          if (item.congViec.parentID === pId && item.key !== tempId) {
            siblings.push(item);
          }
          if (item.childs && item.childs.length > 0) {
            traverse(item.childs as TableRecord[]);
          }
        }
      };
      traverse(records);
      return siblings;
    };

    const siblings = findSiblingsLocal(baseParentRecords, parentRecord.congViec.id);

    const hasManualTiTrongSibling = (s: TableRecord) =>
      s.congViec.tiTrong !== null &&
      s.congViec.tiTrong !== undefined &&
      Number.isFinite(Number(s.congViec.tiTrong));

    const fixedTotal = siblings.filter(hasManualTiTrongSibling).reduce(
      (sum, s) => sum + Number(s.congViec.tiTrong || 0),
      0
    );
    const remaining = Math.max(0, parentTiTrongCal - fixedTotal);
    const autoSiblingCount = siblings.filter((s) => !hasManualTiTrongSibling(s)).length;

    if (siblings.length === 0) {
      tiTrongCal = parentTiTrongCal;
    } else {
      // Cùng quy ước với autoTiTrong: chỉ trừ tiTrong nhập tay; chia đều cho sibling auto + task mới
      tiTrongCal = remaining / (autoSiblingCount + 1);
    }
  }
  const baseTrongKy = DEFAULT_TIEN_DO_TREE as DuAnCongViecTienDoTree;
  const baseLuyKe = DEFAULT_TIEN_DO_TREE as DuAnCongViecTienDoTree;
  const baseRole = parentRecord?.roleItemTree || DEFAULT_ROLE_ITEM_TREE;
  const initialTanSuat = baseParentCongViec.tanSuat ?? 1;
  return {
    idTemp: '',
    hasChange: true,
    key: tempId,
    congViec: {
      isDuAn: false,
      // Ưu tiên: idDuAn truyền vào → treePath[0] của parent (luôn là duan UUID) → congViec.id của duan
      // Không dùng duan?.id vì đó là tên/identifier của dự án, không phải UUID
      duAnID:
        idDuAn ||
        parentRecord?.congViec?.treePath?.split('/')?.[0] ||
        duan?.congViec?.id ||
        '',
      id: tempId,
      tenCongViec: isChild ? 'Công việc con mới' : 'Công việc mới',
      parentID: parentRecord?.congViec.id || idDuAn || baseParentCongViec.parentID || undefined,
      level: level,
      viTri: parentRecord?.childs ? parentRecord.childs.length + 1 : baseParentCongViec.viTri + 1 || 0,
      kanBanMucDoUuTien: baseParentCongViec.kanBanMucDoUuTien || 'low',
      stateApproved: 1,
      chiTieu: baseParentCongViec.chiTieu ?? 100,
      idDoLuong: baseParentCongViec.idDoLuong,
      tanSuat: initialTanSuat,
      nganSach: 0,
      nganSachCal: 0,
      tiTrong: null,
      // Root (con trực tiếp dự án): luôn gói 100. Con dưới CV: dùng phần đã chia (autoTiTrong sẽ cân lại).
      tiTrongCal: isChild ? tiTrongCal : 100,
      tiTrongV2: null,
      tiTrongCalV2: null, // V2: autoTiTrongV2 chia riêng (không mirror V1)
      // Công việc mới trong bảng đều là "con của dự án" (dù isChild=false khi thêm ở root của bảng)
      // nên mặc định luôn "Báo cáo nhập tay" (1).
      hinhThucTongHop: (parentRecord || duan?.congViec?.isDuAn) ? 1 : (baseParentCongViec.hinhThucTongHop ?? 1),
      moTa: '',
      filesDinhKem: baseParentCongViec.filesDinhKem || '',
      nguoiPhuTrachID: defaultPhuTrachId || parentRecord?.congViec.nguoiPhuTrachID || duan?.congViec.nguoiPhuTrachID,
      nguoiHoTroID: ',',
      hasChild: false,
      viecDuocGiao: false,
      showAlert: baseParentCongViec.showAlert ?? false,
      completeQty: baseParentCongViec.completeQty ?? 0,
      kanBanColumnID: baseParentCongViec.kanBanColumnID,
      kanBanViTri: baseParentCongViec.kanBanViTri ?? 0,
      ngayBatDau: baseParentCongViec.ngayBatDau ?? dayjs().hour(12).minute(0).second(0).millisecond(0).toDate(),
      ngayKetThuc: baseParentCongViec.ngayKetThuc ?? dayjs().hour(12).minute(0).second(0).millisecond(0).toDate(),
      site: baseParentCongViec.site || '',
      boPhanPhuTrachID: baseParentCongViec.boPhanPhuTrachID || '',
      year: baseParentCongViec.year || 0,
      nguoiTao: profile?.id ?? '',
      hienThiTrangQuanLy: true,
    } as unknown as DuAnCongViecDTO,
    thoiGian: parentRecord?.thoiGian || '',
    hasInRange: parentRecord?.hasInRange ?? true,
    hasChild: false,
    param: parentRecord?.param || '',
    trongKy: baseTrongKy,
    luyke: baseLuyKe,
    childs: [],
    countChilds: 0,
    tenTanSuat: parentRecord?.tenTanSuat || 'Ngày',
    tenHinhThucTinh: parentRecord?.tenHinhThucTinh || '',
    roleItemTree: baseRole,
    tiTrongDaAn: parentRecord?.tiTrongDaAn || 0,
    tiTrongTatCaDaAn: parentRecord?.tiTrongTatCaDaAn || 0,
    listChiTieu: [],
    parentName: parentRecord?.congViec.tenCongViec,
    temp: true,
    initialTanSuat,
  };
};

const dayjsFromCongViecDate = (value: unknown): Dayjs | null => {
  if (value == null || value === '') return null;
  const d = dayjs(value as string | Date);
  return d.isValid() ? d : null;
};

const dateFromDayjsLocalYmd = (d: Dayjs): Date =>
  toActionPlanCalendarDate(d.format('YYYY-MM-DD')) ?? new Date(d.year(), d.month(), d.date());

/**
 * Sau khi paste: mọi node trong cây dùng đúng khoảng ngày của công việc đích (target / cha paste),
 * không giữ ngày từ bản copy (tránh lệch range).
 */
export const adjustPastedSubtreeDatesToParentRange = (
  subtree: TableRecord,
  targetNgayBatDau: Date | string | null | undefined,
  targetNgayKetThuc: Date | string | null | undefined
): TableRecord => {
  const dStart = dayjsFromCongViecDate(targetNgayBatDau);
  const dEnd = dayjsFromCongViecDate(targetNgayKetThuc);

  if (!dStart && !dEnd) {
    return subtree;
  }

  let ngayBatDau: Date | undefined;
  let ngayKetThuc: Date | undefined;
  if (dStart && dEnd) {
    ngayBatDau = dateFromDayjsLocalYmd(dStart);
    ngayKetThuc = dateFromDayjsLocalYmd(dEnd);
    if (dStart.isAfter(dEnd, 'day')) {
      ngayKetThuc = ngayBatDau;
    }
  } else if (dStart) {
    ngayBatDau = ngayKetThuc = dateFromDayjsLocalYmd(dStart);
  } else if (dEnd) {
    ngayBatDau = ngayKetThuc = dateFromDayjsLocalYmd(dEnd);
  }

  const applyTargetRange = (node: TableRecord): TableRecord => ({
    ...node,
    hasChange: true,
    congViec: {
      ...node.congViec,
      ngayBatDau,
      ngayKetThuc,
    },
    childs: node.childs?.length ? (node.childs as TableRecord[]).map(applyTargetRange) : node.childs,
  });

  return applyTargetRange(subtree);
};

export const calculateOrderNumber = (items: TableRecord[], parentNumber: string = ''): TableRecord[] => {
  return items.map((item, index) => {
    const currentNumber = parentNumber ? `${parentNumber}.${index + 1}` : `${index + 1}`;
    const updatedItem = {
      ...item,
      orderNumber: currentNumber,
    };

    if (item.childs && item.childs.length > 0) {
      const updatedChilds = calculateOrderNumber(item.childs as TableRecord[], currentNumber);
      updatedItem.childs = updatedChilds;
    }

    return updatedItem;
  });
};

/**
 * In-place: gán orderNumber trực tiếp, flatten toàn bộ cây (flat view).
 * @param rootIndexOffset offset cho root (vd. (page-1)*pageSize trên tổng quan phân trang)
 */
export const flattenAllInPlace = (
  items: TableRecord[],
  parentNumber = '',
  rootIndexOffset = 0
): TableRecord[] => {
  const result: TableRecord[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const num = parentNumber ? `${parentNumber}.${i + 1}` : `${rootIndexOffset + i + 1}`;
    item.orderNumber = num;
    result.push(item);
    if (item.childs && item.childs.length > 0) {
      const sub = flattenAllInPlace(item.childs as TableRecord[], num, 0);
      for (let j = 0; j < sub.length; j++) result.push(sub[j]);
    }
  }
  return result;
};

/**
 * In-place: gán orderNumber trực tiếp, flatten theo expandedKeysSet (O(1) lookup).
 * @param rootIndexOffset offset cho root (vd. (page-1)*pageSize trên tổng quan phân trang)
 */
export const flattenExpandedInPlace = (
  items: TableRecord[],
  expandedKeysSet: Set<string>,
  parentNumber = '',
  rootIndexOffset = 0
): TableRecord[] => {
  const result: TableRecord[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const num = parentNumber ? `${parentNumber}.${i + 1}` : `${rootIndexOffset + i + 1}`;
    item.orderNumber = num;
    result.push(item);
    if (expandedKeysSet.has(String(item.key)) && item.childs && item.childs.length > 0) {
      const sub = flattenExpandedInPlace(item.childs as TableRecord[], expandedKeysSet, num, 0);
      for (let j = 0; j < sub.length; j++) result.push(sub[j]);
    }
  }
  return result;
};

export const formatDateValue = (value: Dayjs | null): string => {
  return value ? value.format('DD/MM/YYYY') : '';
};



export const formatCurrency = (value: number): string => {
  return value.toLocaleString('vi-VN', {
    maximumFractionDigits: 3,
  });
};

export const calculateBudgetData = (record: TableRecord) => {
  const nganSach = Number(record.congViec?.nganSach) || 0;

  // Lấy listNganSachThucTe từ listChiTieu hoặc từ originalData
  let listNganSachThucTe: DuAnCongViecNganSachDTO[] = [];
  if (record.congViec?.nganSach && Array.isArray(record.congViec.nganSach)) {
    listNganSachThucTe = record.congViec.nganSach;
  }

  // Tính tổng nganSachThucTe theo công thức: Sum(p => p.NganSachThucTe ?? 0)
  const tongNganSachThucTe =
    Array.isArray(listNganSachThucTe) && listNganSachThucTe.length > 0
      ? listNganSachThucTe.reduce((sum: number, item: DuAnCongViecNganSachDTO) => {
        return sum + (Number(item.nganSachThucTe) || 0);
      }, 0)
      : 0;

  // Công thức: (tongNganSachThucTe / nganSach) * 100 nếu nganSach != null && nganSach != 0
  const phanTramNganSach = nganSach != null && nganSach != 0 ? (tongNganSachThucTe / nganSach) * 100 : 0;

  return {
    plannedBudget: nganSach,
    actualBudget: tongNganSachThucTe,
    displayBudgetPercent: Math.max(0, Math.min(100, Number(phanTramNganSach.toFixed(2)))),
  };
};

export const findRecordById = (items: TableRecord[], id: string): TableRecord | null => {
  const want = String(id);
  for (const item of items) {
    const cvId = item.congViec?.id != null ? String(item.congViec.id) : '';
    const key = item.key != null ? String(item.key) : '';
    const idTemp = item.idTemp != null ? String(item.idTemp) : '';
    if (cvId === want || key === want || idTemp === want) return item;
    if (item.childs && item.childs.length > 0) {
      const found = findRecordById(item.childs as TableRecord[], id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Các `key` của node cha (từ root đến cha trực tiếp) cần expand để thấy công việc `targetCongViecId`.
 */
export const collectAncestorKeysToCongViecId = (
  items: TableRecord[],
  targetCongViecId: string,
  ancestorKeys: string[] = []
): string[] | null => {
  const want = String(targetCongViecId);
  for (const item of items) {
    const cvId = item.congViec?.id != null ? String(item.congViec.id) : '';
    const key = item.key != null ? String(item.key) : '';
    const idTemp = item.idTemp != null ? String(item.idTemp) : '';
    if (cvId === want || key === want || idTemp === want) return ancestorKeys;

    const childs = item.childs as TableRecord[] | undefined;
    if (childs?.length) {
      const found = collectAncestorKeysToCongViecId(childs, want, [...ancestorKeys, String(item.key)]);
      if (found) return found;
    }
  }
  return null;
};

export const getAllKeysWithchilds = (dataSource: TableRecord[]): React.Key[] => {
  const keys: React.Key[] = [];
  const traverse = (items: TableRecord[]) => {
    items.forEach((item) => {
      if (item.childs && item.childs.length > 0) {
        keys.push(item.key);
        traverse(item.childs as TableRecord[]);
      }
    });
  };
  traverse(dataSource);
  return keys;
};

/**
 * Sau reload/merge, expandedRowKeys có thể là id trong khi row.key là treePath (convertToTableData).
 * Map mỗi key đã expand sang key chuẩn trên cây (chỉ node có con mới expand được).
 */
export const resolveExpandedKeysAgainstTableData = (
  dataSource: TableRecord[],
  expandedKeys: React.Key[]
): React.Key[] => {
  if (!expandedKeys.length || !dataSource.length) return [...expandedKeys];

  const aliasToCanonical = new Map<string, React.Key>();

  const visit = (items: TableRecord[]) => {
    for (const item of items) {
      const hasKids = item.childs && item.childs.length > 0;
      if (hasKids) {
        const canonical = item.key;
        aliasToCanonical.set(String(canonical), canonical);
        const id = item.congViec?.id;
        if (id != null && String(id) !== '') {
          aliasToCanonical.set(String(id), canonical);
        }
        const idTemp = item.idTemp;
        if (idTemp != null && String(idTemp) !== '') {
          aliasToCanonical.set(String(idTemp), canonical);
        }
      }
      if (item.childs?.length) visit(item.childs as TableRecord[]);
    }
  };
  visit(dataSource);

  const out: React.Key[] = [];
  const seen = new Set<string>();
  for (const k of expandedKeys) {
    const sk = String(k);
    const canonical = aliasToCanonical.get(sk);
    const resolved = canonical != null ? canonical : k;
    const resolvedKey = String(resolved);
    if (!seen.has(resolvedKey)) {
      seen.add(resolvedKey);
      out.push(resolved);
    }
  }
  return out;
};

const normSiblingParentForReorder = (raw: string | undefined | null, rootParentId: string) => {
  const root = String(rootParentId ?? '').trim();
  const p = String(raw ?? '').trim();
  return !p || p === 'undefined' ? root : p;
};

const reorderSiblingRecordArray = (
  siblings: TableRecord[],
  orderedIds: string[],
  idToViTri: Map<string, number>
): TableRecord[] => {
  const byId = new Map<string, TableRecord>();
  siblings.forEach((s) => {
    const id = String(s?.congViec?.id ?? s?.key ?? '');
    if (id) byId.set(id, s);
  });
  const used = new Set<string>();
  const result: TableRecord[] = [];
  orderedIds.forEach((id, idx) => {
    const record = byId.get(id);
    if (!record) return;
    used.add(id);
    const viTri = idToViTri.has(id) ? Number(idToViTri.get(id)) : idx;
    const viTriChanged = viTriPositionChanged(record.congViec?.viTri, viTri);
    result.push({
      ...record,
      hasChange: viTriChanged ? true : record.hasChange === true,
      congViec: { ...(record.congViec || {}), viTri },
    });
  });
  siblings.forEach((s) => {
    const id = String(s?.congViec?.id ?? s?.key ?? '');
    if (id && !used.has(id)) result.push(s);
  });
  return result;
};

/** Đổi thứ tự mảng sibling trong cây bảng (childs) + viTri. */
export const reorderSiblingsInTableRecords = (
  items: TableRecord[],
  parentId: string,
  rootParentId: string,
  orderedSiblingIds: string[],
  idToViTri?: Map<string, number>
): TableRecord[] => {
  const targetParent = normSiblingParentForReorder(parentId, rootParentId);
  const rootNorm = normSiblingParentForReorder(rootParentId, rootParentId);
  const viTriMap =
    idToViTri ?? new Map(orderedSiblingIds.map((id, idx) => [id, idx]));

  if (targetParent === rootNorm) {
    const placeholders: TableRecord[] = [];
    const siblings: TableRecord[] = [];
    (items || []).forEach((item) => {
      if (item.isPlaceholder) placeholders.push(item);
      else if (normSiblingParentForReorder(item.congViec?.parentID, rootParentId) === targetParent) {
        siblings.push(item);
      }
    });
    const reordered = reorderSiblingRecordArray(siblings, orderedSiblingIds, viTriMap);
    return [...placeholders, ...reordered];
  }

  const walk = (list: TableRecord[]): TableRecord[] =>
    (list || []).map((item) => {
      if (item.isPlaceholder) return item;
      const id = String(item.congViec?.id ?? item?.key ?? '');
      if (id === targetParent) {
        const childs = (item.childs as TableRecord[]) || [];
        const reorderedChilds = reorderSiblingRecordArray(
          childs.filter((c) => !c.isPlaceholder),
          orderedSiblingIds,
          viTriMap
        );
        return { ...item, childs: reorderedChilds };
      }
      if (Array.isArray(item.childs) && item.childs.length > 0) {
        const newChilds = walk(item.childs as TableRecord[]);
        if (newChilds !== item.childs) return { ...item, childs: newChilds };
      }
      return item;
    });

  return walk(items || []);
};

const getTableSiblingOrderedIds = (
  tableRoots: TableRecord[],
  parentId: string,
  rootParentId: string
): string[] => {
  const target = normSiblingParentForReorder(parentId, rootParentId);
  const rootNorm = normSiblingParentForReorder(rootParentId, rootParentId);
  if (target === rootNorm) {
    return (tableRoots || [])
      .filter(
        (record) =>
          !record.isPlaceholder &&
          normSiblingParentForReorder(record.congViec?.parentID, rootParentId) === target
      )
      .map((record) => String(record.congViec?.id ?? record.key ?? ''))
      .filter(Boolean);
  }
  const walk = (list: TableRecord[]): string[] | null => {
    for (const item of list) {
      if (item.isPlaceholder) continue;
      const id = String(item.congViec?.id ?? item.key ?? '');
      if (id === target) {
        return (item.childs as TableRecord[] | undefined)
          ?.filter((c) => !c.isPlaceholder)
          .map((c) => String(c.congViec?.id ?? c.key ?? ''))
          .filter(Boolean) ?? [];
      }
      if (item.childs?.length) {
        const found = walk(item.childs as TableRecord[]);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(tableRoots) ?? [];
};

const reorderCongViecChildrenByIds = (
  children: DuAnCongViecTree[],
  orderedIds: string[]
): DuAnCongViecTree[] => {
  if (!orderedIds.length || children.length < 2) return children;
  const map = new Map(children.map((c) => [String(c.congViec?.id ?? ''), c]));
  const out: DuAnCongViecTree[] = [];
  const used = new Set<string>();
  orderedIds.forEach((id) => {
    const node = map.get(id);
    if (node) {
      out.push(node);
      used.add(id);
    }
  });
  children.forEach((c) => {
    const id = String(c.congViec?.id ?? '');
    if (id && !used.has(id)) out.push(c);
  });
  return out;
};

/** Đồng bộ thứ tự childs congViecData theo cây bảng sau kéo vị trí. */
export const reorderCongViecTreesByTableSiblingOrder = (
  nodes: DuAnCongViecTree[],
  tableRoots: TableRecord[],
  rootParentId: string
): DuAnCongViecTree[] => {
  if (!tableRoots?.length || !nodes?.length) return nodes;

  const walk = (list: DuAnCongViecTree[]): DuAnCongViecTree[] =>
    list.map((node) => {
      const id = String(node.congViec?.id ?? '');
      if (node.childs.length > 0) {
        const orderedIds = getTableSiblingOrderedIds(tableRoots, id, rootParentId);
        const reordered = reorderCongViecChildrenByIds(node.childs as DuAnCongViecTree[], orderedIds);
        const nextChilds = walk(reordered);
        if (nextChilds !== node.childs) {
          return { ...node, childs: nextChilds };
        }
      }
      return node;
    });

  const rootIds = getTableSiblingOrderedIds(tableRoots, rootParentId, rootParentId);
  const reorderedRoots = reorderCongViecChildrenByIds(nodes, rootIds);
  return walk(reorderedRoots);
};

/** Sắp childs theo congViec.viTri sau khi merge API. */
export const sortCongViecTreeChildsByViTri = (nodes: DuAnCongViecTree[]): DuAnCongViecTree[] =>
  (nodes || []).map((node) => {
    let childs = Array.isArray(node.childs) ? [...node.childs] : [];
    if (childs.length > 1) {
      childs = [...childs].sort(
        (a, b) => (Number(a.congViec?.viTri) || 0) - (Number(b.congViec?.viTri) || 0)
      );
    }
    childs = sortCongViecTreeChildsByViTri(childs);
    return childs !== node.childs ? { ...node, childs } : node;
  });

/** Patch listChange sau kéo vị trí — giữ thứ tự cây bảng hiện tại, chỉ cập nhật field + hasChange=false. */
export const applyViTriListChangeToTableRecords = (
  items: TableRecord[],
  responseData: DuAnCongViecTree[],
): TableRecord[] => {
  if (!responseData?.length) return items || [];

  const responseMap = new Map<string, DuAnCongViecTree>();
  const flatten = (nodes: DuAnCongViecTree[]) => {
    (nodes || []).forEach((node) => {
      const id = String(node.congViec?.id ?? '');
      if (id) responseMap.set(id, node);
      if (node.childs?.length) flatten(node.childs);
    });
  };
  flatten(responseData);

  const patchRecord = (records: TableRecord[]): TableRecord[] =>
    (records || []).map((record) => {
      if (record.isPlaceholder) {
        const childs = record.childs?.length
          ? patchRecord(record.childs as TableRecord[])
          : record.childs;
        return childs !== record.childs ? { ...record, childs } : record;
      }
      const id = String(record.congViec?.id ?? record.key ?? '');
      const patch = id ? responseMap.get(id) : undefined;
      let out: TableRecord = record;
      if (patch?.congViec) {
        out = {
          ...record,
          hasChange: false,
          congViec: {
            ...(record.congViec || {}),
            ...patch.congViec,
            viTri: patch.congViec.viTri ?? record.congViec?.viTri,
          },
        };
      }
      if (Array.isArray(out.childs) && out.childs.length > 0) {
        const newChilds = patchRecord(out.childs as TableRecord[]);
        if (newChilds !== out.childs) out = { ...out, childs: newChilds };
      }
      return out;
    });

  return patchRecord(items || []);
};

const buildIdToViTriMapFromCongViecTrees = (changedTrees: DuAnCongViecTree[]): Map<string, number> => {
  const byId = new Map<string, number>();
  const walk = (items: DuAnCongViecTree[]) => {
    (items || []).forEach((it) => {
      const id = String(it.congViec?.id ?? '');
      if (id && typeof it.congViec?.viTri === 'number') {
        byId.set(id, it.congViec.viTri);
      }
      if (it.childs?.length) walk(it.childs);
    });
  };
  walk(changedTrees);
  return byId;
};

/** Cập nhật viTri trên listCongViecDuAnCheck từ listChange API. */
export const patchCheckListViTriFromCongViecTrees = (
  list: CongViecDuAnCheck[],
  changedTrees: DuAnCongViecTree[]
): CongViecDuAnCheck[] => {
  const byId = buildIdToViTriMapFromCongViecTrees(changedTrees);
  if (byId.size === 0) return list;
  return (Array.isArray(list) ? list : []).map((it) => {
    const id = String(it?.id ?? '');
    const viTri = byId.get(id);
    if (viTri === undefined) return it;
    return { ...it, viTri, hasChange: false } as CongViecDuAnCheck;
  });
};

/** Payload editcongviecs khi chỉ kéo vị trí: check có hasChange + tree phẳng không childs. */
export const buildViTriReorderSavePayload = (
  tableRecords: TableRecord[],
  baseCheckList: CongViecDuAnCheck[],
  rootParentId: string,
  dicTienDosByCongViecId?: Record<string, Record<string, TableRecord[]>>
): { listCongViecDuAn: DuAnCongViecTree[]; listCongViecDuAnCheckToSave: CongViecDuAnCheck[] } => {
  const baseById = new Map<string, CongViecDuAnCheck>();
  (Array.isArray(baseCheckList) ? baseCheckList : []).forEach((it) => {
    const id = String(it?.id ?? '');
    if (id) baseById.set(id, it);
  });

  const merged = mergeFullListCongViecDuAnCheck(baseCheckList, tableRecords, rootParentId);
  const listCongViecDuAnCheckToSave: CongViecDuAnCheck[] = [];

  for (const item of merged) {
    const id = String(item?.id ?? '');
    if (!id) continue;
    const base = baseById.get(id);
    const viTriChanged =
      id.startsWith('temp') || (base ? !viTriValuesEqual(item.viTri, base.viTri) : true);
    if (!viTriChanged) continue;
    listCongViecDuAnCheckToSave.push({
      ...item,
      id,
      hasChange: true,
    });
  }

  const listCongViecDuAn: DuAnCongViecTree[] = [];
  for (const checkItem of listCongViecDuAnCheckToSave) {
    const id = String(checkItem.id ?? '');
    const rec = findRecordById(tableRecords, id);
    if (!rec) continue;
    const node = sanitizeRecord(
      { ...rec, hasChange: true, childs: [] } as TableRecord,
      dicTienDosByCongViecId as Record<string, Record<string, DuAnCongViecChiTieuItem[]>>
    );
    listCongViecDuAn.push({
      ...node,
      childs: [],
      countChilds: 0,
      hasChild: Boolean(rec.childs?.length || rec.hasChild),
      hasChange: true,
    });
  }

  return { listCongViecDuAn, listCongViecDuAnCheckToSave };
};

/** Cập nhật viTri + hasChange trên cây bảng theo map id → viTri. */
export const applyViTriByIdMapToTableRecords = (
  items: TableRecord[],
  idToViTri: Map<string, number>
): TableRecord[] => {
  return (items || []).map((item) => {
    if (item.isPlaceholder) {
      const childs = item.childs?.length
        ? applyViTriByIdMapToTableRecords(item.childs as TableRecord[], idToViTri)
        : item.childs;
      return childs !== item.childs ? { ...item, childs } : item;
    }
    const id = String(item?.congViec?.id ?? item.key ?? '');
    const viTri = id ? idToViTri.get(id) : undefined;
    let next: TableRecord = item;
    if (viTri !== undefined) {
      const viTriChanged = viTriPositionChanged(item.congViec?.viTri, viTri);
      next = {
        ...item,
        hasChange: viTriChanged ? true : item.hasChange === true,
        congViec: { ...(item.congViec || {}), viTri },
      };
    }
    if (Array.isArray(next.childs) && next.childs.length > 0) {
      const newChilds = applyViTriByIdMapToTableRecords(next.childs as TableRecord[], idToViTri);
      if (newChilds !== next.childs) {
        next = { ...next, childs: newChilds };
      }
    }
    return next;
  });
};

export const patchCheckListViTriByIdMap = (
  list: CongViecDuAnCheck[],
  idToViTri: Map<string, number>
): CongViecDuAnCheck[] => {
  return (Array.isArray(list) ? list : []).map((record) => {
    const id = String(record?.id ?? '');
    const viTri = id ? idToViTri.get(id) : undefined;
    if (viTri === undefined) return record;
    const viTriChanged = viTriPositionChanged(record?.viTri, viTri);
    return {
      ...record,
      viTri,
      hasChange: viTriChanged ? true : record.hasChange === true,
    } as CongViecDuAnCheck;
  });
};

/** Thu thập id công việc được tick xóa trên bảng (bỏ placeholder). */
export const collectMarkedDeleteIdsFromTable = (records: TableRecord[]): string[] => {
  const ids: string[] = [];
  const walk = (items: TableRecord[]) => {
    (items || []).forEach((item) => {
      if (item.isPlaceholder) {
        if (item.childs?.length) walk(item.childs as TableRecord[]);
        return;
      }
      if (item.isMarkedDelete === true) {
        const id = String(item?.congViec?.id ?? item.key ?? '');
        if (id) ids.push(id);
      }
      if (item.childs?.length) walk(item.childs as TableRecord[]);
    });
  };
  walk(Array.isArray(records) ? records : []);
  return ids;
};

export const updateRecordInTree = (items: TableRecord[], targetId: string, updater: (item: TableRecord) => TableRecord): TableRecord[] => {
  return items.map((item) => {
    const match =
      item.key === targetId || item.congViec?.id === targetId;
    if (match) return updater(item);
    if (item.childs && item.childs.length > 0) {
      return {
        ...item,
        childs: updateRecordInTree(item.childs as TableRecord[], targetId, updater),
      };
    }
    return item;
  });
};

export function isNoopKanbanFieldPatch(patch: TableRecordKanbanFieldPatch): boolean {
  const hasTen = patch.tenCongViec !== undefined;
  const hasStart = patch.ngayBatDau !== undefined;
  const hasEnd = patch.ngayKetThuc !== undefined;
  const hasPhuTrach = patch.nguoiPhuTrachID !== undefined;
  const hasPriority = patch.kanBanMucDoUuTien !== undefined && patch.kanBanMucDoUuTien !== null;
  if (hasTen && !String(patch.tenCongViec ?? '').trim()) return true;
  return !hasTen && !hasStart && !hasEnd && !hasPhuTrach && !hasPriority;
}

export function applyKanbanFieldPatchToTableRecord(
  item: TableRecord,
  patch: TableRecordKanbanFieldPatch
): TableRecord {
  const hasTen = patch.tenCongViec !== undefined;
  const hasStart = patch.ngayBatDau !== undefined;
  const hasEnd = patch.ngayKetThuc !== undefined;
  const hasPhuTrach = patch.nguoiPhuTrachID !== undefined;
  const hasPriority = patch.kanBanMucDoUuTien !== undefined && patch.kanBanMucDoUuTien !== null;
  const rowId = String(item.congViec?.id ?? item.key ?? '');
  const isExisting = rowId && !rowId.startsWith('temp');
  const nextCv = { ...item.congViec } as DuAnCongViecDTO;
  if (hasTen) nextCv.tenCongViec = String(patch.tenCongViec ?? '').trim();
  if (hasStart) nextCv.ngayBatDau = patch.ngayBatDau as Date | undefined;
  if (hasEnd) nextCv.ngayKetThuc = patch.ngayKetThuc as Date | undefined;
  if (hasPhuTrach) {
    const raw = patch.nguoiPhuTrachID;
    nextCv.nguoiPhuTrachID = raw == null || raw === '' ? undefined : String(raw);
  }
  if (hasPriority) nextCv.kanBanMucDoUuTien = String(patch.kanBanMucDoUuTien).toUpperCase();
  return {
    ...item,
    hasChange: true,
    ...(hasPhuTrach && isExisting ? { _phuTrachChanged: true } : {}),
    congViec: nextCv,
  };
}

export function patchTableRecordsByCongViecId(
  records: TableRecord[],
  congViecId: string,
  patch: TableRecordKanbanFieldPatch
): TableRecord[] {
  if (!congViecId || isNoopKanbanFieldPatch(patch)) return records;
  return updateRecordInTree(records, congViecId, (item) => applyKanbanFieldPatchToTableRecord(item, patch));
}

/** Xóa record khỏi cây theo predicate, cập nhật hasChild/countChilds của parent. */
export const removeFromTree = (
  items: TableRecord[],
  predicate: (r: TableRecord) => boolean
): TableRecord[] => {
  return items
    .filter((item) => !predicate(item))
    .map((item) => {
      if (!item.childs?.length) return item;
      const newChilds = removeFromTree(item.childs as TableRecord[], predicate);
      const noChildsLeft = newChilds.length === 0;
      return {
        ...item,
        childs: newChilds,
        hasChild: !noChildsLeft,
        countChilds: newChilds.length,
        ...(noChildsLeft
          ? {
            congViec: {
              ...item.congViec,
              hinhThucTongHop: CONSTANTS.ACTION_PLAN.TABLE.HINH_THUC_TONG_HOP.NHAP_TAY,
            },
          }
          : {}),
      } as TableRecord;
    });
};

/** Tạo placeholder row — loading từ actionPlanTableLoadingAtom (Recoil). */
export const createPlaceholderRecord = (tenantID: string, loading = false): TableRecord =>
({
  idTemp: '',
  key: 'create-placeholder',
  isPlaceholder: true,
  congViec: {
    id: 'undefined',
    tenCongViec: loading ? 'Đang tải...' : 'Tạo công việc',
    site: tenantID,
  } as DuAnCongViecDTO,
  thoiGian: '',
  hasInRange: false,
  hasChild: false,
  param: '',
  trongKy: {} as DuAnCongViecTienDoTree,
  luyke: {} as DuAnCongViecTienDoTree,
  childs: [],
  countChilds: 0,
  tenTanSuat: '',
  tenHinhThucTinh: '',
  roleItemTree: {} as RoleItemTree,
  tiTrongDaAn: 0,
  tiTrongTatCaDaAn: 0,
  hasChange: true,
} as TableRecord);

/** Tìm parent record bằng cách traverse tree */
export const findParentInTree = (items: TableRecord[], childId: string): TableRecord | null => {
  const traverse = (records: TableRecord[], targetId: string, parent?: TableRecord): TableRecord | null => {
    for (const record of records) {
      if (record.congViec.id === targetId || record.key === targetId) {
        return parent || null;
      }
      if (record.childs && record.childs.length > 0) {
        const found = traverse(record.childs as TableRecord[], targetId, record);
        if (found !== null) return found;
      }
    }
    return null;
  };
  return traverse(items, childId);
};

/**
 * Check xem record có phải leaf node không (không có childs)
 */
export const isLeafNode = (record: TableRecord): boolean => {
  return !record.childs || record.childs.length === 0;
};

/**
 * Tính tổng tỉ trọng của các siblings (các childs cùng parent)
 */
export const calculateSiblingsTotalTiTrong = (items: TableRecord[], parentId: string): number => {
  let total = 0;
  const traverse = (records: TableRecord[]) => {
    for (const record of records) {
      if (record.congViec.parentID === parentId) {
        const tiTrong = record.congViec.tiTrong;
        const tiTrongCal = record.congViec.tiTrongCal;
        const value =
          tiTrong !== null && tiTrong !== undefined && Number.isFinite(Number(tiTrong))
            ? Number(tiTrong)
            : tiTrongCal !== null && tiTrongCal !== undefined
              ? Number(tiTrongCal)
              : 0;
        total += value;
      }
      if (record.childs && record.childs.length > 0) {
        traverse(record.childs as TableRecord[]);
      }
    }
  };
  traverse(items);
  return total;
};

/**
 * Cập nhật lại tỉ trọng cho các siblings khi thêm task mới.
 * - Giữ nguyên các dòng đã có `tiTrong` nhập tay (không tính lại).
 * - Chia `tiTrongCal` cho các dòng chưa có `tiTrong` (kể cả sibling đã lưu, có childs):
 *   để khi thêm công việc con mới, vẫn chia lại được phần tỉ trọng cho con (không bị non-leaf “chiếm hết” parent).
 * - Nếu một sibling vừa được đổi `tiTrongCal` mà đã có con: đệ quy chia lại tỉ trọng cho các cấp con bên trong
 *   theo ngân sách mới của nhánh đó.
 */

const getTiTrongValue = (tiTrong: number | null | undefined): number => {
  if (tiTrong !== null && tiTrong !== undefined && Number.isFinite(Number(tiTrong))) {
    return Number(tiTrong);
  }
  return 0;
};

const findSiblings = (records: TableRecord[], parentId: string): TableRecord[] => {
  const siblings: TableRecord[] = [];
  const traverse = (list: TableRecord[]) => {
    for (const item of list) {
      if (item.congViec.parentID === parentId) {
        siblings.push(item);
      }
      if (item.childs && item.childs.length > 0) {
        traverse(item.childs as TableRecord[]);
      }
    }
  };
  traverse(records);
  return siblings;
};
// - `tiTrong` null/undefined: chưa nhập tay (dùng tiTrongCal).
// - `tiTrong` là số hợp lệ (kể cả 0): user nhập tay — tiTrongCal phải null.
export const isManualTiTrongValue = (
  tiTrong: number | null | undefined,
  _tiTrongCal?: number | null | undefined // giữ signature cũ cho call sites
): boolean => {
  void _tiTrongCal;
  if (tiTrong === null || tiTrong === undefined) return false;
  return Number.isFinite(Number(tiTrong));
};

const toRecordId = (r: TableRecord): string => String(r.congViec.id ?? r.key);
const toCheckId = (id: string | number | null | undefined): string => String(id ?? '');

// Cập nhật tiTrongCal cho sibling chưa có tiTrong nhập tay (kể cả có childs).
const updateSiblingsTiTrongCal = (records: TableRecord[], parentId: string, tiTrongCalMap: Map<string, number>, remaining: number): TableRecord[] => {
  return records.map((record) => {
    const isSibling = record.congViec.parentID === parentId;
    const recordId = String(record.congViec.id ?? record.key);

    if (isSibling && tiTrongCalMap.has(recordId)) {
      const nextAutoValue = tiTrongCalMap.get(recordId)!;
      const prevCal = record.congViec?.tiTrongCal;
      const calUnchanged =
        prevCal !== null &&
        prevCal !== undefined &&
        Number.isFinite(Number(prevCal)) &&
        Math.abs(Number(prevCal) - nextAutoValue) < 0.001;
      const prevTiTrong = record.congViec?.tiTrong;
      const tiTrongAlreadyCleared = prevTiTrong === null || prevTiTrong === undefined;
      if (calUnchanged && tiTrongAlreadyCleared) {
        return record;
      }
      return {
        ...record,
        hasChange: true,
        congViec: {
          ...record.congViec,
          // Auto split: chỉ ghi tiTrongCal; xoá tiTrong nhập tay (readme AP V2).
          tiTrong: undefined,
          tiTrongCal: nextAutoValue,
        },
      };
    }
    if (record.childs && record.childs.length > 0) {
      return {
        ...record,
        childs: updateSiblingsTiTrongCal(record.childs as TableRecord[], parentId, tiTrongCalMap, remaining),
      };
    }
    return record;
  });
};

/** Cập nhật tiTrongCalV2 cho sibling (V1 table: root phẳng parentID=duAnId cũng khớp). */
const updateSiblingsTiTrongCalV2 = (
  records: TableRecord[],
  parentId: string,
  tiTrongCalV2Map: Map<string, number>
): TableRecord[] => {
  return records.map((record) => {
    const isSibling = String(record.congViec.parentID ?? '') === String(parentId);
    const recordId = String(record.congViec.id ?? record.key);

    if (isSibling && tiTrongCalV2Map.has(recordId)) {
      const nextAutoValue = tiTrongCalV2Map.get(recordId)!;
      return {
        ...record,
        hasChange: true,
        congViec: {
          ...record.congViec,
          tiTrongV2: undefined,
          tiTrongCalV2: nextAutoValue,
        },
      };
    }
    if (record.childs && record.childs.length > 0) {
      return {
        ...record,
        childs: updateSiblingsTiTrongCalV2(record.childs as TableRecord[], parentId, tiTrongCalV2Map),
      };
    }
    return record;
  });
};

/**
 * Ver 2 độc lập: chia budget cha cho sibling chưa nhập tay tiTrongV2.
 * Dùng findSiblings (không cần node cha trong cây — quan trọng với root V1 dưới duAnId).
 */
function redistributeTiTrongV2UnderParent(
  items: TableRecord[],
  parentId: string,
  parentBudgetV2: number,
  listCongViecDuAnCheck?: CongViecDuAnCheck[],
  fixedIds: string[] = []
): TableRecord[] {
  const budget = Math.max(0, Math.round(parentBudgetV2 * 100) / 100);
  const siblings = findSiblings(items, parentId);
  const checkSiblings = (listCongViecDuAnCheck ?? []).filter(
    (c) => String(c.parentID) === String(parentId)
  );
  const treeIdSet = new Set(siblings.map(toRecordId));

  type Sib = { id: string; tiTrongV2: number | null | undefined; tiTrongCalV2: number | null | undefined };
  const union: Sib[] = [
    ...siblings.map((s) => ({
      id: toRecordId(s),
      tiTrongV2: s.congViec.tiTrongV2 as number | null | undefined,
      tiTrongCalV2: s.congViec.tiTrongCalV2 as number | null | undefined,
    })),
    ...checkSiblings
      .filter((c) => {
        const cid = String(c.id);
        if (treeIdSet.has(cid)) return false;
        if (cid.toLowerCase().startsWith('temp')) return false;
        return true;
      })
      .map((c) => ({
        id: String(c.id),
        tiTrongV2: c.tiTrongV2,
        tiTrongCalV2: c.tiTrongCalV2,
      })),
  ];

  if (union.length === 0) return items;

  const fixedIdSet = new Set(fixedIds.map(String));
  const fixed = union.filter(
    (s) => fixedIdSet.has(s.id) || apv2IsLockedManualTiTrong(s.tiTrongV2, s.tiTrongCalV2)
  );
  const autos = union.filter(
    (s) => !fixedIdSet.has(s.id) && !apv2IsLockedManualTiTrong(s.tiTrongV2, s.tiTrongCalV2)
  );

  const usedByFixed = fixed.reduce((sum, s) => sum + Number(s.tiTrongV2 || 0), 0);
  let remaining = Math.max(0, budget - usedByFixed);

  // Nếu tổng nhập tay > budget → ép chia lại toàn bộ
  if (usedByFixed - budget > 0.01) {
    remaining = budget;
    const allIds = union.map((s) => s.id);
    const n = allIds.length;
    const base = n > 0 ? remaining / n : 0;
    const rounded = Math.round(base * 100) / 100;
    const diff = remaining - rounded * n;
    const calMap = new Map<string, number>();
    allIds.forEach((id, i) => {
      calMap.set(id, i === n - 1 ? Math.round((rounded + diff) * 100) / 100 : rounded);
    });
    return updateSiblingsTiTrongCalV2(items, parentId, calMap);
  }

  const n = autos.length;
  if (n === 0) return items;
  const base = remaining / n;
  const rounded = Math.round(base * 100) / 100;
  const diff = remaining - rounded * n;
  const calMap = new Map<string, number>();
  autos.forEach((s, i) => {
    calMap.set(s.id, i === n - 1 ? Math.round((rounded + diff) * 100) / 100 : rounded);
  });
  return updateSiblingsTiTrongCalV2(items, parentId, calMap);
}

type SiblingDesc =
  | { id: string; inTree: true; record: TableRecord; check?: CongViecDuAnCheck }
  | { id: string; inTree: false; check: CongViecDuAnCheck };

/**
 * Ver 1 only — không đụng cặp V2. Giữ nguyên logic table V1.
 */
export const autoTiTrong = (
  items: TableRecord[],
  parentId: string,
  parentTiTrong: number | null | undefined,
  listCongViecDuAnCheck?: CongViecDuAnCheck[],
  // true khi gọi đệ quy xuống descendants do parent vừa thay đổi.
  // Khi đó nếu tổng hiện tại của children không khớp parent => ép chia lại
  // (dù node đang có `tiTrong`), để `tiTrongCal` của con luôn được cập nhật.
  recalcDescendantsOnMismatch: boolean = false,
  fixedIds: string[] = []
): TableRecord[] => {


  // các công việc cùng cấp
  const siblings = findSiblings(items, parentId);


  // tỉ trọng công việc cha
  const parentTiTrongValue = getTiTrongValue(parentTiTrong);
  // các công việc cùng cấp trong listCongViecDuAnCheck
  const checkSiblings = (listCongViecDuAnCheck ?? []).filter((c) => String(c.parentID) === String(parentId));
  // các công việc cùng cấp trong listCongViecDuAnCheck

  const checkById = new Map<string, CongViecDuAnCheck>();
  checkSiblings.forEach((c) => {
    checkById.set(toCheckId(c.id), c);
  });

  const treeSiblingIdSet = new Set<string>(siblings.map(toRecordId));

  const unionSiblingsOrdered: SiblingDesc[] = [
    ...siblings.map((s) => ({
      id: toRecordId(s),
      inTree: true as const,
      record: s,
      check: checkById.get(toRecordId(s)),
    })),
    ...checkSiblings
      .filter((c) => {
        const cid = toCheckId(c.id);
        if (treeSiblingIdSet.has(cid)) return false;
        // Temp đã xoá khỏi cây — không tính vào pool chia tiTrongCal.
        if (String(cid).toLowerCase().startsWith('temp')) return false;
        return true;
      })
      .map((c) => ({
        id: toCheckId(c.id),
        inTree: false as const,
        check: c,
      })),
  ];

  // --- Detect mismatch (descendants) ---
  const EPS = 0.01;
  // Tổng tỉ trọng các con đã nhập tay (kể cả 0) — dùng để quyết định có phải ép chia lại toàn bộ khi parent đổi không.
  const manualTotal = unionSiblingsOrdered.reduce((sum, s) => {
    const ti = s.inTree ? (s.record.congViec.tiTrong as number | null | undefined) : s.check.tiTrong;
    const cal = s.inTree ? s.record.congViec.tiTrongCal : s.check.tiTrongCal;
    if (!isManualTiTrongValue(ti, cal)) return sum;
    return sum + Number(ti || 0);
  }, 0);

  // Nếu tổng các con đã nhập tay vẫn ≤ parent => giữ nguyên các con đó, chỉ chia lại phần còn lại cho tiTrongCal.
  // Chỉ khi tổng nhập tay > parent (không thể giữ nguyên) mới ép chia lại toàn bộ children.
  const shouldForceAutoOnMismatch =
    recalcDescendantsOnMismatch && manualTotal - parentTiTrongValue > EPS;

  const fixedIdSet = new Set<string>(fixedIds.map(String));

  let fixedSiblings: SiblingDesc[] = [];
  let autoSiblings: SiblingDesc[] = [];
  let remaining: number = parentTiTrongValue;

  if (shouldForceAutoOnMismatch) {
    // ép chia lại toàn bộ ở level này => ignore `tiTrong` hiện có
    fixedSiblings = [];
    autoSiblings = unionSiblingsOrdered;
    remaining = Math.max(0, parentTiTrongValue);
  } else {
    fixedSiblings = unionSiblingsOrdered.filter((s) => {
      if (fixedIdSet.has(s.id)) return true;
      const ti = s.inTree ? (s.record.congViec.tiTrong as number | null | undefined) : s.check.tiTrong;
      const cal = s.inTree ? s.record.congViec.tiTrongCal : s.check.tiTrongCal;
      return isManualTiTrongValue(ti, cal);
    });

    const totalUsedByFixed = fixedSiblings.reduce((sum, s) => {
      const ti = s.inTree ? s.record.congViec.tiTrong : s.check.tiTrong;
      return sum + Number(ti || 0);
    }, 0);

    remaining = Math.max(0, parentTiTrongValue - totalUsedByFixed);

    autoSiblings = unionSiblingsOrdered.filter((s) => {
      if (fixedIdSet.has(s.id)) return false;
      const ti = s.inTree ? (s.record.congViec.tiTrong as number | null | undefined) : s.check.tiTrong;
      const cal = s.inTree ? s.record.congViec.tiTrongCal : s.check.tiTrongCal;
      return !isManualTiTrongValue(ti, cal);
    });
  }
  // Helper: Làm tròn đến 2 chữ số thập phân
  const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
  };

  // Nếu có sibling auto có tiTrongCal: chia phần còn lại đều cho các sibling đó; không thì chia đều mọi auto.
  // Sibling auto không có tiTrongCal (khi pool theo tiTrongCal khác rỗng) nhận 0.
  const hasTiTrongCalForSplit = (s: SiblingDesc): boolean => {
    const cal = s.inTree ? s.record.congViec.tiTrongCal : s.check.tiTrongCal;
    return cal !== null && cal !== undefined && Number.isFinite(Number(cal));
  };
  const recipientsWithCal = autoSiblings.filter(hasTiTrongCalForSplit);
  const pool = recipientsWithCal.length > 0 ? recipientsWithCal : autoSiblings;

  const baseTiTrongCal = pool.length > 0 ? remaining / pool.length : 0;
  const roundedBaseTiTrongCal = roundToTwoDecimals(baseTiTrongCal);

  const totalRounded = roundedBaseTiTrongCal * pool.length;
  const difference = remaining - totalRounded;

  const tiTrongCalMap = new Map<string, number>();
  pool.forEach((sibling, index) => {
    const siblingId = sibling.id;
    const isLastSibling = index === pool.length - 1;
    const finalTiTrongCal = isLastSibling
      ? roundToTwoDecimals(roundedBaseTiTrongCal + difference)
      : roundedBaseTiTrongCal;
    tiTrongCalMap.set(siblingId, finalTiTrongCal);
  });
  for (const s of autoSiblings) {
    if (!tiTrongCalMap.has(s.id)) {
      tiTrongCalMap.set(s.id, 0);
    }
  }
  let result = updateSiblingsTiTrongCal(items, parentId, tiTrongCalMap, remaining);
  // Đệ quy xuống con:
  // - Nếu node có childs thì phải phân bổ lại cho children cấp dưới
  // - Budget: ưu tiên dùng giá trị vừa tính (tiTrongCalMap) nếu có, còn lại lấy target hiện tại của node.
  for (const s of unionSiblingsOrdered) {
    if (!s.inTree) continue;
    if (!Array.isArray(s.record.childs) || s.record.childs.length === 0) continue;

    const sid = s.id;
    const budget = tiTrongCalMap.has(sid) ? tiTrongCalMap.get(sid)! : getCongViecTiTrongTarget(s.record.congViec);
    result = autoTiTrong(result, sid, budget, listCongViecDuAnCheck, true);
  }

  return result;
};

/** Budget V2 của cha: dự án → 100; không thì effective V2 (fallback V1 khi V2 chưa có). */
function resolveParentBudgetV2ForAuto(
  tree: TableRecord[],
  parentId: string,
  listCongViecDuAnCheck?: CongViecDuAnCheck[],
  projectRootId?: string
): number {
  if (projectRootId && String(parentId) === String(projectRootId)) return 100;

  const parent = findRecordById(tree, parentId);
  if (parent?.congViec?.isDuAn) return 100;

  if (parent?.congViec) {
    const fromV2 = getEffectiveWeightVer2(parent.congViec.tiTrongV2, parent.congViec.tiTrongCalV2);
    if (fromV2 > 0) return fromV2;
    const fromV1 = getCongViecTiTrongTarget(parent.congViec);
    if (fromV1 > 0) return fromV1;
  }

  const parentCheck = (listCongViecDuAnCheck ?? []).find((c) => String(c.id) === String(parentId));
  if (parentCheck) {
    const fromV2 = getEffectiveWeightVer2(parentCheck.tiTrongV2, parentCheck.tiTrongCalV2);
    if (fromV2 > 0) return fromV2;
    const fromV1 = getEffectiveWeightVer1(parentCheck.tiTrong, parentCheck.tiTrongCal);
    if (fromV1 > 0) return fromV1;
  }

  return 100;
}

/**
 * Ver 2 only (table V1): chia tiTrongCalV2 theo rule V2 — không đụng tiTrong/tiTrongCal.
 * - Con trực tiếp dự án (projectRootId): chia tổng 100
 * - Cấp dưới: chia budget V2 của cha
 */
export const autoTiTrongV2 = (
  items: TableRecord[],
  parentId: string,
  listCongViecDuAnCheck?: CongViecDuAnCheck[],
  projectRootId?: string,
  fixedIds: string[] = []
): TableRecord[] => {
  const budgetV2 = resolveParentBudgetV2ForAuto(items, parentId, listCongViecDuAnCheck, projectRootId);
  let result = redistributeTiTrongV2UnderParent(
    items,
    parentId,
    budgetV2 > 0 ? budgetV2 : 100,
    listCongViecDuAnCheck,
    fixedIds
  );

  const siblings = findSiblings(result, parentId);
  for (const s of siblings) {
    if (!Array.isArray(s.childs) || s.childs.length === 0) continue;
    const sid = toRecordId(s);
    result = autoTiTrongV2(result, sid, listCongViecDuAnCheck, projectRootId);
  }
  return result;
};

export const findDirectChildrenRecords = (items: TableRecord[], parentId: string): TableRecord[] => {
  const siblings: TableRecord[] = [];
  const traverse = (list: TableRecord[]) => {
    for (const item of list) {
      if (item.congViec.parentID === parentId) {
        siblings.push(item);
      }
      if (item.childs && item.childs.length > 0) {
        traverse(item.childs as TableRecord[]);
      }
    }
  };
  traverse(items);
  return siblings;
};

const roundToTwoDecimalsGlobal = (value: number): number => Math.round(value * 100) / 100;

/**
 * Trần tiTrong nhập được = tỉ trọng cha − tổng tiTrong nhập tay của siblings khác
 * (cây + listCongViecDuAnCheck cùng parent, không trùng id trên cây).
 */
export function computeMaxTiTrongForLeafInput(
  items: TableRecord[],
  parentId: string,
  currentTaskId: string | number,
  parentTargetValue: number,
  listCongViecDuAnCheck?: CongViecDuAnCheck[]
): number {
  const treeSiblings = findDirectChildrenRecords(items, parentId);
  const treeSiblingIdSet = new Set(treeSiblings.map((s) => String(s.congViec.id ?? s.key)));
  const currentIdStr = String(currentTaskId);

  let fixedOthersSum = 0;
  for (const s of treeSiblings) {
    const sid = String(s.congViec.id ?? s.key);
    if (sid === currentIdStr || s.key === String(currentTaskId)) continue;
    if (isManualTiTrongValue(s.congViec.tiTrong, s.congViec.tiTrongCal)) {
      fixedOthersSum += Number(s.congViec.tiTrong);
    }
  }
  const checkSiblings = (listCongViecDuAnCheck ?? []).filter((c) => String(c.parentID) === String(parentId));
  for (const c of checkSiblings) {
    const cid = String(c.id);
    if (cid === currentIdStr) continue;
    if (treeSiblingIdSet.has(cid)) continue;
    if (cid.toLowerCase().startsWith('temp')) continue;
    if (isManualTiTrongValue(c.tiTrong, c.tiTrongCal)) {
      fixedOthersSum += Number(c.tiTrong ?? 0);
    }
  }
  return Math.max(0, roundToTwoDecimalsGlobal(parentTargetValue - fixedOthersSum));
}

/** Tỉ trọng mục tiêu V2 (ưu tiên tiTrongV2, không thì tiTrongCalV2). */
export const getCongViecTiTrongTargetV2 = (cv: TableRecord['congViec']): number => {
  if (cv.tiTrongV2 !== null && cv.tiTrongV2 !== undefined && Number.isFinite(Number(cv.tiTrongV2))) {
    return Number(cv.tiTrongV2);
  }
  if (cv.tiTrongCalV2 !== null && cv.tiTrongCalV2 !== undefined && Number.isFinite(Number(cv.tiTrongCalV2))) {
    return Number(cv.tiTrongCalV2);
  }
  return 0;
};

/**
 * Trần tiTrongV2 nhập được = budget V2 cha − tổng tiTrongV2 nhập tay siblings khác.
 */
export function computeMaxTiTrongV2ForLeafInput(
  items: TableRecord[],
  parentId: string,
  currentTaskId: string | number,
  parentTargetValue: number,
  listCongViecDuAnCheck?: CongViecDuAnCheck[]
): number {
  const treeSiblings = findDirectChildrenRecords(items, parentId);
  const treeSiblingIdSet = new Set(treeSiblings.map((s) => String(s.congViec.id ?? s.key)));
  const currentIdStr = String(currentTaskId);

  let fixedOthersSum = 0;
  for (const s of treeSiblings) {
    const sid = String(s.congViec.id ?? s.key);
    if (sid === currentIdStr || s.key === String(currentTaskId)) continue;
    if (apv2IsLockedManualTiTrong(s.congViec.tiTrongV2, s.congViec.tiTrongCalV2)) {
      fixedOthersSum += Number(s.congViec.tiTrongV2);
    }
  }
  const checkSiblings = (listCongViecDuAnCheck ?? []).filter((c) => String(c.parentID) === String(parentId));
  for (const c of checkSiblings) {
    const cid = String(c.id);
    if (cid === currentIdStr) continue;
    if (treeSiblingIdSet.has(cid)) continue;
    if (cid.toLowerCase().startsWith('temp')) continue;
    if (apv2IsLockedManualTiTrong(c.tiTrongV2, c.tiTrongCalV2)) {
      fixedOthersSum += Number(c.tiTrongV2 ?? 0);
    }
  }
  return Math.max(0, roundToTwoDecimalsGlobal(parentTargetValue - fixedOthersSum));
}

/**
 * Gốc cây vừa paste không được giữ tổng tỉ trọng copy (vd 100); gán tiTrongCal = phần còn lại của cha
 * sau khi trừ các anh em cùng cấp (để tổng con = tỉ trọng cha).
 */
export const normalizePastedSubtreeRootTiTrong = (
  items: TableRecord[],
  parentId: string,
  pastedRootId: string,
  parentTarget: number
): TableRecord[] => {
  const siblings = findDirectChildrenRecords(items, parentId);
  const isPastedRoot = (s: TableRecord) =>
    String(s.congViec.id) === String(pastedRootId) || String(s.key) === String(pastedRootId);
  const usedByOthers = siblings.filter((s) => !isPastedRoot(s)).reduce((sum, s) => sum + getRecordDisplayTiTrong(s), 0);
  const allocated = Math.max(0, parentTarget - usedByOthers);

  const patch = (records: TableRecord[]): TableRecord[] =>
    records.map((r) => {
      if (isPastedRoot(r)) {
        return {
          ...r,
          hasChange: true,
          congViec: {
            ...r.congViec,
            tiTrong: undefined,
            tiTrongCal: allocated,
          },
        };
      }
      if (r.childs && r.childs.length > 0) {
        return { ...r, childs: patch(r.childs as TableRecord[]) };
      }
      return r;
    });

  return patch(items);
};

function replaceRecordInTreeById(
  items: TableRecord[],
  targetId: string,
  mapper: (n: TableRecord) => TableRecord
): TableRecord[] {
  return items.map((item) => {
    if (String(item.congViec.id) === String(targetId) || String(item.key) === String(targetId)) {
      return mapper(item);
    }
    if (item.childs && item.childs.length > 0) {
      return { ...item, childs: replaceRecordInTreeById(item.childs as TableRecord[], targetId, mapper) };
    }
    return item;
  });
}

/**
 * Trong cây vừa paste: mỗi cha chia đều tỉ trọng (tiTrongCal) cho mọi con, đệ quy xuống lá.
 * Xóa tiTrong để không giữ số từ bản gốc lúc copy (thường 100 trên node có con).
 * Khác autoTiTrong — hàm đó chỉ cân một cấp anh em; hàm này đệ quy cả subtree sau paste.
 */
export const equalSplitTiTrongInPastedSubtree = (items: TableRecord[], subtreeRootId: string): TableRecord[] => {
  const roundToTwo = (value: number) => Math.round(value * 100) / 100;
  const hasManualTiTrong = (s: TableRecord) =>
    s.congViec.tiTrong !== null &&
    s.congViec.tiTrong !== undefined &&
    Number.isFinite(Number(s.congViec.tiTrong));

  const equalizeSubtree = (node: TableRecord): TableRecord => {
    const kids = (node.childs || []) as TableRecord[];
    if (kids.length === 0) {
      return node;
    }
    const budget = getCongViecTiTrongTarget(node.congViec);
    const manualKids = kids.filter(hasManualTiTrong);
    const autoKids = kids.filter((k) => !hasManualTiTrong(k));
    const manualUsed = manualKids.reduce((sum, k) => sum + Number(k.congViec.tiTrong || 0), 0);
    const remaining = Math.max(0, budget - manualUsed);
    const n = autoKids.length;
    const baseCal = n > 0 ? roundToTwo(remaining / n) : 0;

    const newKids: TableRecord[] = kids.map((c) => {
      if (hasManualTiTrong(c)) {
        // Giữ nguyên dòng đã có tiTrong, chỉ đệ quy xuống con.
        return equalizeSubtree(c);
      }
      const autoIndex = autoKids.findIndex(
        (k) => String(k.congViec.id || k.key) === String(c.congViec.id || c.key)
      );
      const cal =
        autoIndex === n - 1 ? roundToTwo(remaining - baseCal * (n - 1)) : baseCal;
      const patched: TableRecord = {
        ...c,
        hasChange: true,
        congViec: {
          ...c.congViec,
          tiTrongCal: Math.max(0, cal),
        },
      };
      return equalizeSubtree(patched);
    });

    return { ...node, childs: newKids };
  };

  return replaceRecordInTreeById(items, subtreeRootId, (n) => equalizeSubtree(n));
};

/**
 * Convert DuAnCongViecTree to Task format
 * Mục tiêu: level 0, Dự án (công việc ngoài cùng): level 1, Children: không hiển thị
 */
export const convertToTasks = (
  data: DuAnCongViecTree[],
  mucTieuMap?: Map<string, string>,
  listMucTieuOrder?: string[],
  listMucTieuPhuTrachIDs?: string[]
): Task[] => {
  // Group data theo mtctid để tạo mục tiêu
  const groupedByMtctid: { [key: string]: DuAnCongViecTree[] } = {};

  data.forEach((item) => {
    const mtctid = item.congViec.mtctid || 'unknown';
    if (!groupedByMtctid[mtctid]) {
      groupedByMtctid[mtctid] = [];
    }
    groupedByMtctid[mtctid].push(item);
  });

  const tasks: Task[] = [];

  // Sắp xếp các mtctid theo thứ tự của listMucTieuOrder
  const sortedMtctids = listMucTieuOrder
    ? listMucTieuOrder
      .filter((mtctid) => groupedByMtctid[mtctid])
      .concat(Object.keys(groupedByMtctid).filter((mtctid) => !listMucTieuOrder.includes(mtctid)))
    : Object.keys(groupedByMtctid);

  // Tạo row mục tiêu cho mỗi group theo thứ tự đã sắp xếp
  sortedMtctids.forEach((mtctid) => {
    const items = groupedByMtctid[mtctid];
    if (!items) return;
    const firstItem = items[0];

    // Convert các dự án (level 0, isDuAn = true) thành level 1
    const duAnItems = items.filter((item) => {
      const cv = item.congViec;
      return cv.level === 0 && cv.isDuAn;
    });

    // Tính tổng cho mục tiêu
    const totalCount = items.reduce((sum, item) => sum + (item.countChilds || 0), 0);
    // Tiến độ mục tiêu = (tổng tiến độ quy đổi theo số childs) / (tổng childs)

    // Tạo row mục tiêu (level 0)
    const mucTieuName = mucTieuMap?.get(mtctid) || `Mục tiêu ${mtctid}`;
    tasks.push({
      id: `muc-tieu-${mtctid}`,
      taskName: mucTieuName,
      thoiGian: '',
      hoanThanh: 0,
      trangThai: 0,
      countChilds: totalCount,
      nganSachThucHien: 0,
      nguoiPhuTrachID: '',
      nguoiHoTroID: '',
      nguoiTao: '',
      icon: null,
      mtctid: mtctid,
      completionCount: totalCount,
      completionPercentage: 0,
      dueDate: '',
      assignedTo: '',
      support: '',
      creator: '',
      budgetPercentage: 0,
      status: 'pending',
      isGoalRow: true,
      level: 0,
      originalData: firstItem,
    });

    // Thêm các dự án (công việc ngoài cùng) - level 1
    duAnItems.forEach((duAnItem: DuAnCongViecTree) => {
      const duAnCongViec = duAnItem.congViec;
      const trongKy = duAnItem.trongKy;
      const plannedBudget = duAnCongViec.nganSach || 0;
      const actualBudget = duAnCongViec.nganSachThucHien || 0;
      const budgetPercentage =
        plannedBudget > 0 ? Math.min(100, Math.max(0, (actualBudget / plannedBudget) * 100)) : 0;

      const mtctid = duAnCongViec.mtctid || '';
      const isMucTieuKhongPhuTrach = Boolean(
        listMucTieuPhuTrachIDs && mtctid && !listMucTieuPhuTrachIDs.includes(mtctid)
      );
      const totalProgress = trongKy.hoanThanh ?? 0;
      tasks.push({
        id: duAnCongViec.id,
        taskName: duAnCongViec.tenCongViec || '',
        thoiGian: duAnItem.thoiGian || '',
        hoanThanh: Math.round(trongKy.hoanThanh || 0),
        trangThai: trongKy.trangThai || 0,
        countChilds: duAnItem.countChilds || 0,
        nganSachThucHien: actualBudget,
        nguoiPhuTrachID: duAnCongViec.nguoiPhuTrachID || '',
        nguoiHoTroID: duAnCongViec.nguoiHoTroID || '',
        nguoiTao: duAnCongViec.nguoiTao || '',
        icon: trongKy.icon || null,
        mtctid,
        completionCount: duAnItem.countChilds || 0,
        completionPercentage: totalProgress,
        dueDate: duAnItem.thoiGian || '',
        assignedTo: '',
        support: '',
        creator: '',
        budgetPercentage,
        status: trongKy.trangThai === 3 ? 'success' : trongKy.trangThai === 2 ? 'active' : 'pending',
        isGoalRow: false,
        level: 1,
        originalData: duAnItem,
        isMucTieuKhongPhuTrach,
      });
    });
  });

  return tasks;
};

/** Gom dòng mục tiêu + các dự án ngay sau thành cây để Table có expand/collapse */
export function nestTasksByGoalRow(flat: Task[]): Task[] {
  const result: Task[] = [];
  let currentParent: Task | null = null;

  for (const row of flat) {
    if (row.isGoalRow) {
      const { ...rest } = row;
      currentParent = { ...rest, children: [] };
      result.push(currentParent);
    } else if (currentParent?.children) {
      currentParent.children.push(row);
    } else {
      result.push(row);
    }
  }

  return result.map((node) => {
    if (node.isGoalRow && (!node.children || node.children.length === 0)) {
      const { ...rest } = node;
      return rest as Task;
    }
    return node;
  });
}

/**
 * Get icon path from trongKy.icon - up/down from assets, default to down
 * Returns the icon path string instead of JSX for better separation of concerns
 */
export const getStatusIconPath = (originalData?: DuAnCongViecTree): string => {
  if (!originalData?.trongKy?.icon) {
    return '/icon/down.png';
  }

  const iconName = originalData.trongKy.icon.toLowerCase();
  if (iconName.includes('up')) {
    return '/icon/up.png';
  }

  return '/icon/down.png';
};




/** Navigate đến công việc đang chọn: TableView doc va expand + highlight + scroll đến khi tìm kiếm inline. */
export function setQuanLySessionCongViecId(congViecId: string) {
  if (typeof window === 'undefined' || !congViecId) return;
  try {
    window.sessionStorage.setItem(
      CONSTANTS.ACTION_PLAN.SESSION_KEY_QUANLY,
      JSON.stringify({ congviecID: congViecId })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

//----------------------------------------------------------------------------------------|
//--------------------------------- DỰ ÁN CÔNG VIỆC --------------------------------------|
//----------------------------------------------------------------------------------------|


/** Ghi listChiTieu / hinhThuc / chiTieu sau chia chỉ tiêu (chỉ nhánh persistViaParentSave) */
export function applyChiaChiTieu(
  items: TableRecord[],
  recordId: string,
  patch: { listChiTieu?: DuAnCongViecChiTieuItem[]; hinhThuc?: number; chiTieu?: number }

): TableRecord[] {

  const rid = String(recordId);
  return items.map((item) => {
    const id = String(item.congViec?.id ?? item.key ?? '');
    if (id === rid) {
      const mergeListChiTieu = (prevList: DuAnCongViecChiTieuItem[] | null | undefined, incoming: DuAnCongViecChiTieuItem[] | null | undefined): DuAnCongViecChiTieuItem[] | null | undefined => {
        if (!incoming) return prevList;
        const prev = Array.isArray(prevList) ? prevList : [];
        const byKey = new Map<string, DuAnCongViecChiTieuItem>();
        const keyOf = (x: DuAnCongViecChiTieuItem): string => {
          const d = x?.ngay;
          if (d == null || d === '') return '';
          return formatDateOnlyLocalYMD(d as Date | string) ?? '';
        };
        prev.forEach((x) => {
          const k = keyOf(x);
          if (k) byKey.set(k, x);
        });
        incoming.forEach((x) => {
          const k = keyOf(x);
          if (!k) return;
          const old = byKey.get(k);
          byKey.set(k, old ? { ...old, ...x } : x);
        });
        return Array.from(byKey.values());
      };
      // Đổi tần suất (chưa sync initialTanSuat) → thay toàn bộ lịch kỳ, không merge kỳ cũ.
      const tanSuatChanged =
        item.initialTanSuat !== undefined &&
        item.initialTanSuat !== null &&
        Number(item.congViec?.tanSuat) !== Number(item.initialTanSuat);
      const nextListChiTieu =
        patch.listChiTieu != null && tanSuatChanged
          ? patch.listChiTieu
          : mergeListChiTieu(item.listChiTieu as DuAnCongViecChiTieuItem[] | null, patch.listChiTieu as DuAnCongViecChiTieuItem[] | null);
      return {
        ...item,
        hasChange: true,
        // Sau chia chỉ tiêu: coi tần suất hiện tại là baseline → tắt icon cảnh báo
        initialTanSuat: item.congViec?.tanSuat,
        listChiTieu: nextListChiTieu,
        congViec: {
          ...item.congViec,
          ...(patch.hinhThuc !== undefined ? { hinhThuc: patch.hinhThuc } : {}),
          ...(patch.chiTieu !== undefined ? { chiTieu: patch.chiTieu } : {}),
        },
      };
    }
    if (item.childs && item.childs.length > 0) {
      return {
        ...item,
        childs: applyChiaChiTieu(item.childs as TableRecord[], recordId, patch),
      };
    }
    return item;
  });
}
export function isCellEditing(
  editingRef: MutableRefObject<{ rowKey: string; colKey: string } | null>,
  record: TableRecord,
  colKey: string,
): boolean {
  const rowKey = getRecordRowKey(record);
  return editingRef.current?.rowKey === rowKey && editingRef.current?.colKey === colKey;
}

export function getRowDndId(record: TableRecord): string {
  return String(record.congViec?.id ?? record.key ?? '');
}

export const toViTriDragId = (rowId: string) => `drag:${rowId}`;
export const toViTriDropId = (rowId: string) => `drop:${rowId}`;

export const parseViTriDragId = (id: string | number) => String(id).replace(/^drag:/, '');
export const parseViTriDropId = (id: string | number) => String(id).replace(/^drop:/, '');

export function getRecordRowKey(record: TableRecord): string {
  return String(record.key ?? record.congViec.id ?? '');
}

/** Key ổn định cho row bảng — khớp convertToTableData (treePath ưu tiên). */
export function getTableRecordStableKey(
  record: Pick<TableRecord, 'key' | 'congViec'>,
  congViecPatch?: Partial<DuAnCongViec>
): string {
  const cv = congViecPatch ? { ...record.congViec, ...congViecPatch } : record.congViec;
  const treePath = cv?.treePath;
  if (treePath) return String(treePath);
  if (record.key) return String(record.key);
  const id = cv?.id;
  return id ? String(id) : '';
}

export function recordHasExpandableChildren(
  record: TableRecord,
  matched?: DuAnCongViecTree
): boolean {
  return (
    (record.childs?.length ?? 0) > 0 ||
    record.hasChild === true ||
    matched?.hasChild === true ||
    matched?.congViec?.hasChild === true
  );
}

export function expandedRowKeysSignature(keys: React.Key[]): string {
  return [...new Set(keys.map((k) => String(k)))].sort().join('|');
}

export function mergeExpandedRowKeys(prev: React.Key[], extra: React.Key[]): React.Key[] {
  const next = Array.from(
    new Set([...prev.map((k) => String(k)), ...extra.map((k) => String(k))])
  ) as React.Key[];
  return expandedRowKeysSignature(prev) !== expandedRowKeysSignature(next) ? next : prev;
}

/** Path ổn định để prune expand: treePath → row.key → id. */
export function resolveRecordExpandPath(
  record: {
    key?: React.Key;
    congViec?: { id?: string | null; treePath?: string | null } | null;
  } | null | undefined
): string {
  if (!record) return '';
  const tp = String(record.congViec?.treePath ?? '').trim();
  if (tp) return tp;
  const key = String(record.key ?? '').trim();
  if (key) return key;
  return String(record.congViec?.id ?? '').trim();
}

/** Alias expand (row.key + treePath + id + idTemp) của node và mọi descendant — dùng khi collapse. */
export function collectSubtreeExpandKeyAliases(
  record: {
    key?: React.Key;
    idTemp?: string | null;
    congViec?: { id?: string | null; treePath?: string | null } | null;
    childs?: Array<{
      key?: React.Key;
      idTemp?: string | null;
      congViec?: { id?: string | null; treePath?: string | null } | null;
      childs?: unknown[];
    }> | null;
  } | null | undefined
): Set<string> {
  const into = new Set<string>();
  const walk = (node: typeof record) => {
    if (!node) return;
    if (node.key != null && String(node.key) !== '') into.add(String(node.key));
    const tp = node.congViec?.treePath;
    if (tp != null && String(tp) !== '') into.add(String(tp));
    const id = node.congViec?.id;
    if (id != null && String(id) !== '') into.add(String(id));
    const idTemp = node.idTemp;
    if (idTemp != null && String(idTemp) !== '') into.add(String(idTemp));
    const kids = node.childs;
    if (kids?.length) {
      for (const c of kids) walk(c as typeof record);
    }
  };
  walk(record);
  return into;
}

/**
 * Collapse: bỏ self + descendants.
 * - Walk childs local (nếu đã lazy-load)
 * - + mọi key dạng treePath dưới path / chứa segment id (kể cả childs chưa load)
 */
export function removeExpandedKeysUnderCollapsedRecord(
  prev: React.Key[],
  record: {
    key?: React.Key;
    idTemp?: string | null;
    congViec?: { id?: string | null; treePath?: string | null } | null;
    childs?: Array<{
      key?: React.Key;
      idTemp?: string | null;
      congViec?: { id?: string | null; treePath?: string | null } | null;
      childs?: unknown[];
    }> | null;
  } | null | undefined
): React.Key[] {
  if (!prev.length) return prev;
  const aliases = collectSubtreeExpandKeyAliases(record);
  const path = resolveRecordExpandPath(record);
  const pathPrefix = path ? `${path}/` : '';
  const id = String(record?.congViec?.id ?? '').trim();

  return prev.filter((key) => {
    const s = String(key ?? '').trim();
    if (!s) return false;
    if (aliases.has(s)) return false;
    if (path && (s === path || (pathPrefix && s.startsWith(pathPrefix)))) return false;
    if (id) {
      // Self
      if (s === id || s.endsWith(`/${id}`)) return false;
      // Descendant path chứa segment id (kể cả khi row.key ≠ full treePath)
      if (s.startsWith(`${id}/`) || s.includes(`/${id}/`)) return false;
    }
    return true;
  });
}

/** congViec.id trong subtree (kể cả root) — clear loadedChildren khi collapse. */
export function collectSubtreeCongViecIds(
  record: {
    congViec?: { id?: string | null } | null;
    childs?: Array<{ congViec?: { id?: string | null } | null; childs?: unknown[] }> | null;
  } | null | undefined
): string[] {
  const ids: string[] = [];
  const walk = (node: typeof record) => {
    if (!node) return;
    const id = String(node.congViec?.id ?? '').trim();
    if (id) ids.push(id);
    const kids = node.childs;
    if (kids?.length) {
      for (const c of kids) walk(c as typeof record);
    }
  };
  walk(record);
  return ids;
}

/**
 * Giữ expanded keys còn trỏ node đang có trên forest (sau rehydrate).
 * Khớp key / treePath / id / segment cuối.
 */
export function pruneExpandedKeysToForest(
  keys: React.Key[],
  forest: Array<{
    key?: React.Key;
    congViec?: { id?: string | null; treePath?: string | null } | null;
    childs?: unknown[] | null;
  }>
): React.Key[] {
  if (!keys.length) return [];
  const present = new Set<string>();
  const walk = (nodes: typeof forest) => {
    for (const n of nodes || []) {
      if (n.key != null && String(n.key) !== '') present.add(String(n.key));
      const tp = n.congViec?.treePath;
      if (tp != null && String(tp) !== '') present.add(String(tp));
      const id = n.congViec?.id;
      if (id != null && String(id) !== '') present.add(String(id));
      const kids = n.childs as typeof forest | undefined;
      if (kids?.length) walk(kids);
    }
  };
  walk(forest);
  if (present.size === 0) return [];

  const out: React.Key[] = [];
  const seen = new Set<string>();
  for (const k of keys) {
    const s = String(k ?? '').trim();
    if (!s || seen.has(s)) continue;
    const last = s.includes('/') ? s.split('/').filter(Boolean).pop() || s : s;
    if (present.has(s) || present.has(last)) {
      seen.add(s);
      out.push(k);
    }
  }
  return out;
}

/**
 * Trước rehydrate tong-quan: bỏ path-key không thuộc root đang load.
 * Bare id (không có `/`) giữ nguyên — nested expand có thể chỉ lưu id khi TreePath trống.
 */
export function filterExpandedKeysUnderOverviewRoots(
  keys: React.Key[],
  roots: Array<{
    key?: React.Key;
    congViec?: { id?: string | null; treePath?: string | null } | null;
  }>
): React.Key[] {
  if (!keys.length) return [];
  if (!roots.length) return [];

  const rootIds = new Set<string>();
  const rootPaths: string[] = [];
  for (const r of roots) {
    const id = String(r.congViec?.id ?? '').trim();
    if (id) rootIds.add(id);
    const path = resolveRecordExpandPath(r);
    if (path) rootPaths.push(path);
  }
  if (rootIds.size === 0 && rootPaths.length === 0) return [...keys];

  return keys.filter((key) => {
    const s = String(key ?? '').trim();
    if (!s) return false;
    if (!s.includes('/')) {
      // Bare id: giữ nếu là root, hoặc giữ tạm (nested không path) — chỉ drop rõ ràng không phải root
      // khi đã có path-keys dưới root… Thực tế: giữ tất cả bare id để không gãy nested.
      return true;
    }
    const first = s.split('/').filter(Boolean)[0] || '';
    if (first && rootIds.has(first)) return true;
    return rootPaths.some((rp) => s === rp || s.startsWith(`${rp}/`));
  });
}

export function remapExpandedRowKeysForTableData(
  tableData: TableRecord[],
  prevKeys: React.Key[],
  keyRemap?: Map<string, string>
): React.Key[] {
  const remapped = prevKeys.map((k) => keyRemap?.get(String(k)) ?? k);
  return resolveExpandedKeysAgainstTableData(tableData, remapped);
}
/** Format date từ Date / ISO / DD-MM-YYYY → hiển thị DD/MM/YYYY (ngày lịch Action Plan). */
export function formatDateDisplay(value?: string | Date | null): string {
  return formatActionPlanDateDisplay(value ?? null);
}

// KANBAN 

export function duAnToBoardRules(du: DuAnCongViecDTO): KanbanBoardRulesState | null {
  if (!du || typeof du !== 'object') return null;
  const toN = (v: unknown): number | null => {
    if (v === undefined || v === null || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };
  return {
    kanBanParentColumnMode: toN(du.kanBanParentColumnMode),
    kanBanChildColumnMode: toN(du.kanBanChildColumnMode),
  };
}